/**
 * Demo conversations for the CasePreviewModal.
 * Each post ID maps to an array of chat messages demonstrating the ability.
 * Supports rich content: workflow steps, tool calls, sub-agents, rich cards.
 */

import type { RichCard, SubAgent } from "@/lib/agent-demo-data"

export interface WorkflowStep {
  stepNumber: number
  stepName: string
  status: "done" | "running" | "pending"
  description?: string
}

export interface ToolCallDisplay {
  tool: string
  status: "done" | "running"
  result?: string
}

// Simplified SubAgent for basic demos (legacy)
export interface SubAgentDisplay {
  name: string
  role: string
  status: "done" | "running" | "pending"
  currentAction?: string
}

export interface DemoMessage {
  role: "user" | "assistant" | "system"
  content?: string

  // Rich content (optional)
  thinking?: boolean
  workflowStep?: WorkflowStep
  toolCalls?: ToolCallDisplay[]
  subAgents?: (SubAgent | SubAgentDisplay)[]  // Support both full and simplified
  richCards?: RichCard[]
  summaryItems?: string[]
}

export const DEMO_CONVERSATIONS: Record<string, DemoMessage[]> = {
  /* ── Skills ── */

  "p-001": [
    { role: "user", content: "帮我翻译这段话：The bull market is driven by FOMO and retail investors are chasing the momentum." },
    { role: "assistant", content: "翻译结果：\n\n牛市由 FOMO（错失恐惧症）驱动，散户投资者正在追涨。\n\n术语说明：\n• bull market → 牛市（非「公牛市场」）\n• FOMO → 错失恐惧症\n• chasing the momentum → 追涨" },
    { role: "user", content: "那 bear trap 和 dead cat bounce 呢？" },
    { role: "assistant", content: "• bear trap → 空头陷阱（价格短暂下跌后反弹，诱骗做空者）\n• dead cat bounce → 死猫反弹（暴跌后的短暂技术性反弹，不代表趋势反转）\n\n这些都是金融领域的习语，直译完全不通。" },
  ],

  "p-002": [
    { role: "user", content: "帮我分析这条评论：好好好，原来是这么玩的是吧" },
    { role: "assistant", content: "情感判定：负面（置信度 92%）\n\n分析：这是典型的中文互联网阴阳怪气表达。连续的「好」字并非真正赞同，而是带有不满和讽刺语气，类似于「行，你厉害」。" },
    { role: "user", content: "那这条呢：虽然但是，还真被你说对了" },
    { role: "assistant", content: "情感判定：正面（置信度 78%）\n\n分析：「虽然但是」是年轻人口头禅，表示心理上有些不服但理性上认同。整体表达的是认可和赞同，带一点「不太情愿承认」的俏皮感。" },
  ],

  "p-003": [
    { role: "user", content: "我有一篇用 Markdown 写的技术文档，大概 5000 字，需要转成 PDF 给客户看。" },
    { role: "assistant", content: "没问题！请上传你的 Markdown 文件。你想用哪个模板？\n\n1. 学术论文模板（IEEE/ACM 格式）\n2. 技术文档模板（代码高亮 + 自动目录）\n3. 商务报告模板（简约风格 + 公司 Logo 位）" },
    { role: "user", content: "用技术文档模板吧" },
    { role: "assistant", content: "已生成 PDF！包含：\n✅ 自动目录（3 级标题）\n✅ 代码块语法高亮（检测到 Python 和 SQL）\n✅ 页眉：文档标题 | 页脚：页码\n✅ 总共 12 页，含 3 张图表\n\n点击下载：技术文档_v1.pdf" },
  ],

  "p-004": [
    { role: "user", content: "老板让我明天开会前看完这份 80 页的行业报告，我只关心结论和关键数据。" },
    { role: "assistant", content: "收到！已为你生成结构化摘要（关注维度：结论 + 关键数据）：\n\n📊 核心结论：\n1. 2025年国内AI市场规模达4200亿元，同比增长31%\n2. 大模型应用层企业融资占比首次超过基础设施层\n3. 预计2026年将出现3-5家百亿级AI原生应用公司\n\n📈 关键数据：\n• 企业AI采用率：从38%升至67%\n• 平均部署周期：从6个月缩短至2.3个月\n• 投资回报期：中位数8个月\n\n完整摘要共1200字，需要展开哪个部分？" },
  ],

  "p-005": [
    { role: "user", content: "帮我查一下上个月深圳地区 VIP 用户的复购率" },
    { role: "assistant", content: "已生成 SQL（MySQL 方言）：\n\n```sql\nSELECT \n  COUNT(DISTINCT CASE WHEN o2.user_id IS NOT NULL THEN o1.user_id END) * 100.0 \n  / COUNT(DISTINCT o1.user_id) AS repurchase_rate\nFROM orders o1\nJOIN users u ON o1.user_id = u.id\nLEFT JOIN orders o2 ON o1.user_id = o2.user_id \n  AND o2.created_at > o1.created_at\n  AND o2.created_at < DATE_ADD(o1.created_at, INTERVAL 30 DAY)\nWHERE u.vip_level > 0\n  AND u.city = '深圳'\n  AND o1.created_at BETWEEN '2026-02-01' AND '2026-02-28';\n```\n\n逻辑说明：在2月有过购买的深圳VIP用户中，30天内再次购买的比例。" },
  ],

  "p-006": [
    { role: "user", content: "帮我看看这组K线数据有什么形态" },
    { role: "assistant", content: "检测到以下形态：\n\n🔴 看跌吞没（Bearish Engulfing）@ 第12根K线\n置信度：87% | 信号：看空\n说明：大阴线完全包裹前一根阳线，通常出现在上涨末端。\n\n🟢 锤子线（Hammer）@ 第18根K线\n置信度：72% | 信号：潜在反转\n说明：下影线是实体的2倍以上，出现在下跌趋势中，可能预示反弹。\n\n建议结合成交量和均线确认信号。" },
  ],

  "p-007": [
    { role: "user", content: "帮我写个正则：匹配中国大陆手机号" },
    { role: "assistant", content: "```regex\n1[3-9]\\d{9}\n```\n\n说明：匹配以1开头、第二位是3-9、后面跟9位数字的11位手机号。覆盖移动/联通/电信所有号段。\n\n如果需要更严格的匹配（带国家代码），可以用：\n```regex\n(?:\\+?86)?1[3-9]\\d{9}\n```" },
    { role: "user", content: "反过来，帮我解释一下这个正则是什么意思：^(?=.*[A-Z])(?=.*\\d)[A-Za-z\\d@$!%*?&]{8,}$" },
    { role: "assistant", content: "这个正则的意思是：\n\n密码强度验证规则——\n✅ 至少包含一个大写字母\n✅ 至少包含一个数字\n✅ 只允许字母、数字和特殊符号（@$!%*?&）\n✅ 总长度至少8位\n\n典型用途：注册页面的密码强度校验。" },
  ],

  "p-008": [
    { role: "user", content: "我写了一篇关于「远程办公效率」的文章，目标是公众号读者，帮我起标题" },
    {
      role: "assistant",
      content: "好的！公众号标题需要平衡吸引力和专业度。让我先加载标题优化工具...",
      subAgents: [{
        id: "skills-loader",
        name: "技能加载器",
        role: "环境准备",
        status: "done",
        skills: [
          {
            name: "热点追踪",
            icon: "trending-up",
            description: "实时热搜与话题分析",
            status: "done",
            delay: 200,
            toolCalls: [
              { tool: "fetchTrendingTopics", label: "获取热搜", category: "search", status: "done", result: "已获取微信/知乎/小红书热搜榜", delay: 200 },
              { tool: "analyzeKeywords", label: "提取关键词", category: "ai", status: "done", result: "提取关键词：远程办公、效率、居家工作", delay: 400 }
            ]
          },
          {
            name: "情绪分析",
            icon: "brain",
            description: "标题情绪强度评估",
            status: "done",
            delay: 400,
            toolCalls: [
              { tool: "loadEmotionModel", label: "加载情绪模型", category: "ai", status: "done", result: "加载情绪词典与强度模型", delay: 200 },
              { tool: "calibrateIntensity", label: "校准阈值", category: "ai", status: "done", result: "校准好奇/共鸣/焦虑等情绪阈值", delay: 400 }
            ]
          },
          {
            name: "点击率预测",
            icon: "target",
            description: "标题 CTR 预估模型",
            status: "done",
            delay: 600,
            toolCalls: [
              { tool: "loadCTRModel", label: "加载CTR模型", category: "ai", status: "done", result: "加载基于 10 万+样本的 CTR 模型", delay: 200 },
              { tool: "initPlatformRules", label: "初始化规则", category: "data", status: "done", result: "初始化公众号平台规则库", delay: 400 }
            ]
          }
        ]
      }]
    },
    {
      role: "system",
      workflowStep: {
        stepNumber: 1,
        stepName: "受众分析",
        status: "done",
        description: "分析公众号读者画像与阅读习惯"
      }
    },
    {
      role: "assistant",
      toolCalls: [
        { tool: "analyzeAudience", status: "done", result: "识别目标受众：25-40岁职场人，关注效率提升" },
        { tool: "extractPainPoints", status: "done", result: "提取痛点：摸鱼、拖延、工作生活边界模糊" }
      ]
    },
    {
      role: "system",
      workflowStep: {
        stepNumber: 2,
        stepName: "标题生成",
        status: "done",
        description: "基于心理钩子生成候选标题"
      }
    },
    {
      role: "assistant",
      toolCalls: [
        { tool: "generateTitles", status: "done", result: "生成 30 个候选标题" },
        { tool: "predictCTR", status: "done", result: "预测点击率并排序" },
        { tool: "filterCompliance", status: "done", result: "过滤低俗标题，保留 Top 10" }
      ]
    },
    {
      role: "assistant",
      content: "已为你生成 10 个标题候选（公众号风格，按预测 CTR 排序）：",
      richCards: [
        {
          type: "code",
          title: "标题优化结果",
          language: "markdown",
          code: `1. 远程办公三年，我总结了普通人都能用的 5 个效率心法
   预测 CTR: 7.8% | 心理钩子: 经验权威 + 数字列表 | 情绪强度: ⭐⭐⭐

2. 在家办公总想摸鱼？试试这套被 500 强企业验证过的方法
   预测 CTR: 7.5% | 心理钩子: 痛点共鸣 + 权威背书 | 情绪强度: ⭐⭐⭐⭐

3. 别再假装努力了：远程办公真正高效的人都在做这件事
   预测 CTR: 7.2% | 心理钩子: 认知颠覆 + 好奇心 | 情绪强度: ⭐⭐⭐⭐

4. 为什么有人远程办公效率翻倍，而你却越来越累？
   预测 CTR: 6.9% | 心理钩子: 对比反差 + 自我反思 | 情绪强度: ⭐⭐⭐

5. 远程办公的第一年，我踩过的 7 个效率陷阱
   预测 CTR: 6.6% | 心理钩子: 避坑指南 + 数字列表 | 情绪强度: ⭐⭐⭐

6. 那些远程办公还能升职加薪的人，都掌握了这个秘密
   预测 CTR: 6.3% | 心理钩子: 利益点 + 好奇心 | 情绪强度: ⭐⭐⭐

7. 远程办公效率低？可能是你的工作环境出了问题
   预测 CTR: 6.0% | 心理钩子: 问题诊断 + 解决方案 | 情绪强度: ⭐⭐

8. 从拖延症重度患者到效率达人，我的远程办公逆袭之路
   预测 CTR: 5.7% | 心理钩子: 逆袭故事 + 情感共鸣 | 情绪强度: ⭐⭐⭐

9. 远程办公 = 自由？这 3 个真相没人告诉你
   预测 CTR: 5.4% | 心理钩子: 认知纠偏 + 数字列表 | 情绪强度: ⭐⭐⭐

10. 高效远程办公指南：让老板看到你的价值
    预测 CTR: 5.1% | 心理钩子: 实用指南 + 职场利益 | 情绪强度: ⭐⭐`
        }
      ],
      summaryItems: [
        "生成了 10 个标题，平均预测 CTR 6.5%",
        "心理钩子分布：痛点共鸣 4 个，好奇心 3 个，权威背书 2 个，利益点 1 个",
        "情绪强度：高强度 3 个，中强度 6 个，低强度 1 个",
        "建议 A/B 测试前 3 个标题，预计整体点击率提升 35-45%"
      ]
    }
  ],

  "p-009": [
    { role: "user", content: "我完全不会写代码，真的能做 AI Skill 吗？" },
    { role: "assistant", content: "完全可以！我当初也是纯文科背景。做 Skill 的核心其实是写好 Prompt，不需要写代码。\n\n第一步：想清楚你的 Skill 要解决什么问题\n第二步：在 SafeClaw 的 Skill 编辑器里写 System Prompt\n第三步：测试调整，发布到社区\n\n最简单的 Skill 十分钟就能做完。你想做什么类型的？" },
    { role: "user", content: "我想做一个帮人写年终总结的" },
    { role: "assistant", content: "这个选题很好，职场刚需！建议这样设计：\n\n1. 让用户输入今年做了什么（流水账也行）\n2. Skill 自动提炼亮点、量化成果、组织结构\n3. 输出一份老板爱看的年终总结\n\n核心 Prompt 就是教 AI 怎么把平铺直叙变成「结果导向」的职场表达。要不要我帮你看看 Prompt 怎么写？" },
  ],

  "p-010": [
    { role: "user", content: "帮我脱敏这段文本：客户张三，手机号13812345678，身份证号440106199001011234，邮箱zhangsan@qq.com" },
    { role: "assistant", content: "脱敏结果：\n\n客户张*，手机号138****5678，身份证号4401**********1234，邮箱zha****@qq.com\n\n识别并处理了：\n• 姓名 ×1\n• 手机号 ×1\n• 身份证号 ×1\n• 邮箱 ×1\n\n脱敏规则：保留首尾特征字符，中间用*替换。" },
  ],

  /* ── Tools ── */

  "p-011": [
    { role: "user", content: "我刚和 AI 讨论了项目架构，帮我同步到 Notion" },
    { role: "assistant", content: "已检测到本次对话的关键信息：\n\n📋 同步内容：\n• 标题：项目架构讨论\n• 数据库：技术笔记\n• 标签：#架构 #后端\n• 提取了3个决策点和2个待办事项\n\n已同步至你的 Notion 数据库「技术笔记」，条目ID: TN-0234。" },
  ],

  "p-012": [
    // ── Round 1: 需求确认 ──
    { role: "user", content: "我们公司有 300 多篇飞书文档要迁移到 Notion，能批量导出成 Markdown 吗？" },
    { role: "assistant", content: "可以，飞书文档批量导出我做过很多次了。先确认几个细节：\n\n1. **导出范围** — 是整个知识库空间，还是指定文件夹？\n2. **图片处理** — 飞书的图片链接导出后会失效，需要下载到本地。你希望图片放在哪？\n3. **表格** — 飞书的多维表格比较特殊，普通文档里的表格可以转 Markdown 表格，但多维表格建议导出成 CSV\n4. **权限** — 需要你授权飞书 API 的只读权限，我不会做任何写入操作\n\n另外，300 多篇大概需要 10-15 分钟，期间你可以去忙别的。" },

    // ── Round 2: 确认参数 ──
    { role: "user", content: "整个「产品研发」空间都要。图片下载到本地 /images 目录就行，多维表格导 CSV。" },
    { role: "assistant", content: "收到，确认导出配置：\n\n• 范围：「产品研发」空间全部文档\n• 输出格式：Markdown（.md）\n• 图片：下载至 ./images/，文档内链接自动替换为本地相对路径\n• 多维表格：导出为 CSV，普通表格转 Markdown 表格\n• 目录结构：保持飞书原有的文件夹层级\n\n请授权飞书 API 只读权限，我开始扫描文档列表。" },

    // ── Round 3: 扫描 + 问题发现 ──
    { role: "user", content: "已授权，开始吧" },
    {
      role: "assistant",
      content: "扫描完成，发现一些情况需要确认：",
      richCards: [
        {
          type: "table",
          title: "文档扫描结果",
          columns: [
            { key: "type", label: "类型", align: "left" },
            { key: "count", label: "数量", align: "right" },
            { key: "note", label: "备注", align: "left" },
          ],
          rows: [
            { type: "普通文档", count: "268", note: "正常导出" },
            { type: "多维表格", count: "23", note: "→ 导出 CSV" },
            { type: "思维导图", count: "12", note: "⚠️ 转为缩进文本" },
            { type: "空文档/草稿", count: "9", note: "建议跳过" },
            { type: "含密码的文档", count: "3", note: "⚠️ 需手动解密" },
          ],
        },
      ],
    },
    { role: "user", content: "空文档跳过，加密的也先跳过，后面我手动处理。思维导图转文本没问题。" },
    { role: "assistant", content: "好，跳过 12 篇（9 空文档 + 3 加密），实际导出 303 篇。开始批量处理..." },

    // ── Round 4: 导出完成 + 质量报告 ──
    {
      role: "assistant",
      content: "全部导出完成！耗时 11 分 42 秒。",
      richCards: [
        {
          type: "breakdown",
          title: "导出结果（共 303 篇）",
          total: 303,
          unit: "篇",
          items: [
            { label: "完美导出", count: 287, color: "#10b981" },
            { label: "有格式损失", count: 12, color: "#f59e0b" },
            { label: "跳过", count: 4, color: "#6b7280" },
          ],
        },
        {
          type: "table",
          title: "格式损失详情",
          columns: [
            { key: "file", label: "文件", align: "left" },
            { key: "issue", label: "问题", align: "left" },
            { key: "fix", label: "建议", align: "left" },
          ],
          rows: [
            { file: "API设计规范.md", issue: "嵌套表格丢失", fix: "手动补充表格" },
            { file: "Q3复盘.md", issue: "投票组件无法转换", fix: "已转为文本列表" },
            { file: "产品路线图.md", issue: "甘特图丢失", fix: "已导出为任务列表" },
          ],
        },
      ],
    },

    // ── Round 5: 后续优化 ──
    { role: "user", content: "287 篇完美导出挺好的。不过我看了几篇，有的文档标题是飞书自动生成的，能批量重命名吗？" },
    {
      role: "assistant",
      content: "可以。我扫描了一下，有 34 篇文档的文件名是飞书自动生成的格式（如 `feishu_2025xxxx_abc123.md`），其余都是正常标题。\n\n重命名策略：\n- 用文档内的一级标题（# 开头的第一行）作为文件名\n- 如果没有一级标题，用前 30 个字符\n- 去掉特殊字符，空格替换为连字符\n\n示例：`feishu_20250812_a3f2c1.md` → `API鉴权方案-v2.md`\n\n34 篇全部处理好了。另外我顺手生成了一份目录索引文件 `INDEX.md`，按原来飞书的文件夹层级整理的，方便你导入 Notion 时对照。",
      richCards: [
        {
          type: "deliverables",
          title: "导出文件包",
          files: [
            { name: "feishu-export/", type: "folder", size: "287 篇 Markdown" },
            { name: "feishu-export/images/", type: "folder", size: "1,847 张图片" },
            { name: "feishu-export/tables/", type: "folder", size: "23 个 CSV" },
            { name: "INDEX.md", type: "md", size: "12 KB" },
          ],
        },
      ],
    },
  ],

  "p-013": [
    { role: "user", content: "帮我查一下沪深300和中证500的实时指数" },
    { role: "assistant", content: "查询结果（2026-03-14 14:30:00 UTC+8）：\n\n| 指数 | 最新点位 | 24h涨跌 |\n|--------|----------|--------|\n| 沪深300   | 3,841.50 | +1.23% |\n| 中证500    | 5,238.20 | +1.21% |\n\n两个指数同步走强，市场情绪偏乐观。" },
  ],

  "p-014": [
    { role: "user", content: "帮我截一下 apple.com 的完整页面" },
    { role: "assistant", content: "截图完成！\n\n📸 配置：\n• URL: https://apple.com\n• 视口：1440×900\n• 等待：页面完全加载 + 动画结束\n• 输出尺寸：1440×8720px\n\n文件已保存为 apple_com_full.png（2.3MB）。" },
  ],

  "p-015": [
    { role: "user", content: "帮我导出《思考，快与慢》的所有划线笔记" },
    { role: "assistant", content: "已从微信读书导出《思考，快与慢》的笔记：\n\n📖 统计：\n• 划线：47 条\n• 想法：12 条\n• 覆盖章节：26/38 章\n\n已按章节整理成 Markdown 格式，每条划线附带页码和你的批注。文件：思考快与慢_笔记.md" },
  ],

  "p-016": [
    { role: "user", content: "这个 CSV 有很多问题，帮我清洗一下" },
    { role: "assistant", content: "扫描完成！发现以下问题：\n\n🔍 数据质量报告（共 15,230 行）：\n• 空值：342 个（集中在「电话」列，占 2.2%）\n• 重复行：28 行\n• 日期格式不统一：153 个（混用 yyyy/MM/dd 和 yyyy-MM-dd）\n• 数字异常：12 个（金额列出现中文「万」字）\n\n已自动修复并生成 cleaned_data.csv。原始文件已备份。" },
  ],

  /* ── Workflows ── */

  "p-017": [
    { role: "user", content: "我需要为科技类公众号生成一篇关于 AI 手机的文章" },
    {
      role: "assistant",
      content: "收到！正在加载内容创作技能...",
      subAgents: [
        {
          id: "skills-loader",
          name: "技能加载器",
          role: "环境准备",
          status: "done",
          skills: [
            { name: "热点追踪", icon: "trending", description: "实时热点监测与分析", status: "done", delay: 200, toolCalls: [{ tool: "TrendMonitor.init", label: "初始化热点雷达", category: "general", result: "就绪", status: "done", delay: 200 }] },
            { name: "内容生成", icon: "edit", description: "AI写作与润色", status: "done", delay: 400, toolCalls: [{ tool: "ContentWriter.load", label: "加载写作引擎", category: "general", result: "就绪", status: "done", delay: 400 }] },
            { name: "SEO优化", icon: "search", description: "关键词优化与排名", status: "done", delay: 600, toolCalls: [{ tool: "SEO.init", label: "初始化SEO工具", category: "general", result: "就绪", status: "done", delay: 600 }] }
          ]
        }
      ]
    },
    { role: "system", workflowStep: { stepNumber: 1, stepName: "热点雷达", status: "done" } },
    {
      role: "assistant",
      toolCalls: [
        { tool: "热点追踪", status: "done", result: "检测到 AI 手机话题热度上升 34%" },
        { tool: "竞品分析", status: "done", result: "已收集 12 篇相关报道" }
      ]
    },
    { role: "system", workflowStep: { stepNumber: 2, stepName: "选题角度确认", status: "done" } },
    {
      role: "assistant",
      content: "📋 选题分析完成\n\n当前热点：AI 手机 + 各品牌发布会密集期\n建议角度：「AI 手机到底是革命还是噱头？」\n\n优势：用户关注度高，争议性强，适合引发讨论"
    },
    { role: "user", content: "可以，继续" },
    { role: "system", workflowStep: { stepNumber: 3, stepName: "大纲生成", status: "done" } },
    {
      role: "assistant",
      content: "✅ 大纲已生成",
      richCards: [
        {
          type: "breakdown",
          title: "文章结构（预计 2800 字）",
          items: [
            { label: "开篇：AI 手机的三大卖点", count: 600 },
            { label: "实测：真的智能还是伪需求", count: 900 },
            { label: "对比：传统旗舰 vs AI 手机", count: 700 },
            { label: "结论：值不值得买", count: 600 }
          ]
        }
      ]
    },
    { role: "system", workflowStep: { stepNumber: 4, stepName: "正文扩写", status: "done" } },
    { role: "assistant", content: "正在扩写正文... 已完成 2800 字初稿" },
    { role: "system", workflowStep: { stepNumber: 5, stepName: "标题 A/B 测试", status: "done" } },
    {
      role: "assistant",
      content: "🎯 标题候选（按预估点击率排序）",
      richCards: [
        {
          type: "table",
          title: "标题测试结果",
          columns: [
            { key: "title", label: "标题", align: "left" },
            { key: "ctr", label: "预估点击率", align: "right" }
          ],
          rows: [
            { title: "AI 手机用了三个月，我发现了这些真相", ctr: { value: "8.2%", trend: "up" } },
            { title: "花 6000 买 AI 手机，到底值不值？", ctr: { value: "7.5%", trend: "up" } },
            { title: "AI 手机是革命还是噱头？实测告诉你", ctr: "6.8%" }
          ]
        }
      ]
    },
    { role: "system", workflowStep: { stepNumber: 6, stepName: "排版终检", status: "done" } },
    {
      role: "assistant",
      summaryItems: [
        "文章已完成：2800 字，4 个章节",
        "推荐标题：「AI 手机用了三个月，我发现了这些真相」",
        "配图建议：3 张对比图 + 1 张总结表格",
        "预计阅读时长：6 分钟"
      ]
    }
  ],

  "p-018": [
    { role: "user", content: "我想回测一个双均线交叉策略，MA5 上穿 MA20 做多，下穿做空" },
    {
      role: "assistant",
      content: "收到！正在加载量化交易相关技能...",
      subAgents: [
        {
          id: "skills-loader",
          name: "技能加载器",
          role: "环境准备",
          status: "done",
          skills: [
            {
              name: "量化回测引擎",
              icon: "chart",
              description: "策略回测与信号生成",
              status: "done",
              delay: 200,
              toolCalls: [
                { tool: "BacktestEngine.init", label: "初始化回测引擎", category: "general", result: "就绪", status: "done", delay: 200 }
              ]
            },
            {
              name: "金融数据分析",
              icon: "database",
              description: "K线数据获取与处理",
              status: "done",
              delay: 400,
              toolCalls: [
                { tool: "DataFeed.connect", label: "连接数据源", category: "data", result: "已连接", status: "done", delay: 400 }
              ]
            },
            {
              name: "技术指标计算",
              icon: "calculator",
              description: "均线、MACD等指标计算",
              status: "done",
              delay: 600,
              toolCalls: [
                { tool: "Indicators.load", label: "加载指标库", category: "general", result: "50+指标可用", status: "done", delay: 600 }
              ]
            }
          ]
        }
      ]
    },
    { role: "system", workflowStep: { stepNumber: 1, stepName: "策略假设文档", status: "done" } },
    {
      role: "assistant",
      content: "📝 策略假设记录\n\n信号：MA5 上穿 MA20 → 做多，下穿 → 做空\n假设依据：短期均线反映趋势拐点\n预期：趋势跟踪策略，适合单边行情"
    },
    { role: "system", workflowStep: { stepNumber: 2, stepName: "历史数据获取", status: "done" } },
    {
      role: "assistant",
      toolCalls: [
        { tool: "数据源连接", status: "done", result: "已连接券商行情 API" },
        { tool: "K线数据下载", status: "done", result: "螺纹钢主力 1H，近 6 个月" }
      ]
    },
    {
      role: "assistant",
      content: "✅ 数据获取完成\n\n标的：螺纹钢主力合约\n周期：1H\n范围：近 6 个月\n共获取：4,320 根 K 线"
    },
    { role: "system", workflowStep: { stepNumber: 3, stepName: "交易信号生成", status: "done" } },
    { role: "system", workflowStep: { stepNumber: 4, stepName: "回测引擎执行", status: "done" } },
    {
      role: "assistant",
      content: "回测执行中...",
      toolCalls: [
        { tool: "均线计算", status: "done", result: "MA5 和 MA20 已计算" },
        { tool: "信号识别", status: "done", result: "识别 67 次交叉信号" },
        { tool: "模拟交易", status: "done", result: "回测完成" }
      ]
    },
    { role: "system", workflowStep: { stepNumber: 5, stepName: "收益与风控分析", status: "done" } },
    {
      role: "assistant",
      content: "📈 回测结果",
      richCards: [
        {
          type: "table",
          title: "策略表现",
          columns: [
            { key: "metric", label: "指标", align: "left" },
            { key: "value", label: "数值", align: "right" }
          ],
          rows: [
            { metric: "总收益率", value: { value: "+18.7%", trend: "up" } },
            { metric: "最大回撤", value: { value: "-12.3%", trend: "down" } },
            { metric: "夏普比率", value: "1.42" },
            { metric: "胜率", value: "43%" },
            { metric: "交易次数", value: "67 次" },
            { metric: "平均持仓", value: "3.2 天" }
          ]
        },
        {
          type: "alert",
          items: [
            { level: "safe", label: "策略类型", value: "赢多亏少型", detail: "胜率不高但盈亏比好" },
            { level: "warning", label: "风险提示", value: "震荡市表现差", detail: "适合单边趋势行情" }
          ]
        }
      ]
    },
    {
      role: "assistant",
      summaryItems: [
        "回测周期：6 个月，4,320 根 K 线",
        "总收益率：+18.7%，最大回撤 -12.3%",
        "夏普比率：1.42（风险调整后收益良好）",
        "交易次数：67 次，胜率 43%",
        "建议：结合成交量和趋势强度过滤信号"
      ]
    }
  ],

  "p-019": [
    { role: "user", content: "我刚提了一个 PR，帮我审查一下" },
    {
      role: "assistant",
      content: "收到！正在加载代码审查技能...",
      subAgents: [
        {
          id: "skills-loader",
          name: "技能加载器",
          role: "环境准备",
          status: "done",
          skills: [
            { name: "静态代码分析", icon: "code", description: "代码质量与规范检查", status: "done", delay: 200, toolCalls: [{ tool: "ESLint.init", label: "初始化代码检查", category: "general", result: "就绪", status: "done", delay: 200 }] },
            { name: "安全漏洞扫描", icon: "shield", description: "SQL注入、XSS等漏洞检测", status: "done", delay: 400, toolCalls: [{ tool: "SecurityScanner.load", label: "加载安全规则", category: "general", result: "OWASP Top 10", status: "done", delay: 400 }] },
            { name: "性能分析", icon: "zap", description: "性能瓶颈与优化建议", status: "done", delay: 600, toolCalls: [{ tool: "Profiler.init", label: "初始化性能分析", category: "general", result: "就绪", status: "done", delay: 600 }] }
          ]
        }
      ]
    },
    { role: "system", workflowStep: { stepNumber: 1, stepName: "静态分析扫描", status: "done" } },
    {
      role: "assistant",
      toolCalls: [
        { tool: "ESLint 检查", status: "done", result: "0 错误，2 个 warning" },
        { tool: "TypeScript 类型检查", status: "done", result: "通过" }
      ]
    },
    { role: "system", workflowStep: { stepNumber: 2, stepName: "逻辑边界检查", status: "done" } },
    {
      role: "assistant",
      content: "⚠️ 发现 1 个逻辑问题",
      richCards: [
        {
          type: "alert",
          items: [
            { level: "warning", label: "空值判断缺失", value: "userService.ts:45", detail: "`user.email` 可能为 undefined" }
          ]
        }
      ]
    },
    { role: "system", workflowStep: { stepNumber: 3, stepName: "安全漏洞扫描", status: "done", description: "通过" } },
    { role: "system", workflowStep: { stepNumber: 4, stepName: "性能瓶颈分析", status: "done" } },
    {
      role: "assistant",
      content: "⚠️ 性能问题",
      richCards: [
        {
          type: "alert",
          items: [
            { level: "warning", label: "N+1 查询", value: "getOrders()", detail: "建议改用 JOIN" }
          ]
        }
      ]
    },
    { role: "system", workflowStep: { stepNumber: 5, stepName: "结构化审查报告", status: "done" } },
    {
      role: "assistant",
      content: "📋 PR #247 审查报告",
      summaryItems: [
        "变更：+342/-89 行，涉及 8 个文件",
        "静态分析：通过（0 错误，2 个 warning）",
        "逻辑检查：发现 1 个问题（空值判断）",
        "安全扫描：通过",
        "性能分析：发现 N+1 查询问题",
        "建议：修复后再合并"
      ]
    }
  ],

  "p-020": [
    { role: "user", content: "甲方说「下周三给我竞品分析」，我说好的（内心毫无波动）" },
    {
      role: "assistant",
      content: "收到！正在加载竞品分析技能...",
      subAgents: [
        {
          id: "skills-loader",
          name: "技能加载器",
          role: "环境准备",
          status: "done",
          skills: [
            { name: "竞品情报", icon: "search", description: "竞品数据采集与分析", status: "done", delay: 200, toolCalls: [{ tool: "CompetitorDB.init", label: "初始化竞品数据库", category: "data", result: "就绪", status: "done", delay: 200 }] },
            { name: "数据可视化", icon: "bar-chart", description: "对比图表与矩阵生成", status: "done", delay: 400, toolCalls: [{ tool: "ChartEngine.load", label: "加载图表引擎", category: "general", result: "就绪", status: "done", delay: 400 }] },
            { name: "报告生成", icon: "file-text", description: "结构化报告输出", status: "done", delay: 600, toolCalls: [{ tool: "ReportBuilder.init", label: "初始化报告模板", category: "general", result: "就绪", status: "done", delay: 600 }] }
          ]
        }
      ]
    },
    { role: "system", workflowStep: { stepNumber: 1, stepName: "分析目标确认", status: "done" } },
    { role: "assistant", content: "📋 分析目标确认\n\n主体产品：SafeClaw\n竞品：Notion\n分析维度：功能、定价、用户群、技术架构" },
    { role: "system", workflowStep: { stepNumber: 2, stepName: "信息采集", status: "done" } },
    {
      role: "assistant",
      toolCalls: [
        { tool: "产品官网爬取", status: "done", result: "Notion 功能列表已采集" },
        { tool: "定价方案分析", status: "done", result: "4 个定价档位" },
        { tool: "用户评价采集", status: "done", result: "App Store + G2 评分" }
      ]
    },
    { role: "system", workflowStep: { stepNumber: 3, stepName: "功能对比矩阵", status: "done" } },
    {
      role: "assistant",
      content: "📊 功能对比矩阵",
      richCards: [
        {
          type: "table",
          title: "核心功能对比",
          columns: [
            { key: "feature", label: "功能", align: "left" },
            { key: "safeclaw", label: "SafeClaw", align: "center" },
            { key: "notion", label: "Notion", align: "center" }
          ],
          rows: [
            { feature: "AI 原生", safeclaw: "✅ 核心", notion: "🔶 附加" },
            { feature: "多 Agent 协作", safeclaw: "✅", notion: "❌" },
            { feature: "文档协作", safeclaw: "🔶 基础", notion: "✅ 强项" },
            { feature: "数据库", safeclaw: "🔶 简单", notion: "✅ 强大" },
            { feature: "API 集成", safeclaw: "✅", notion: "✅" }
          ]
        }
      ]
    },
    {
      role: "assistant",
      summaryItems: [
        "SafeClaw 优势：AI 原生、多 Agent 协作",
        "Notion 优势：文档协作、数据库功能",
        "定价：SafeClaw 更灵活，Notion 企业版较贵",
        "目标用户：SafeClaw 偏技术团队，Notion 更通用",
        "完整报告已生成（12 页 PDF）"
      ]
    }
  ],

  "p-021": [
    { role: "user", content: "帮我读这篇论文：Attention Is All You Need" },
    { role: "assistant", content: "论文阅读工作流启动 📚\n\n【3分钟速览】\n• 类型：会议论文（NeurIPS 2017）\n• 核心创新：提出 Transformer 架构，完全基于注意力机制，抛弃了 RNN/CNN\n• 关键结果：英德翻译 BLEU +2.0，训练时间缩短至 3.5 天\n\n【核心论点】\n1. 自注意力机制可以替代循环结构捕获长距离依赖\n2. 多头注意力允许模型同时关注不同子空间\n3. 位置编码解决了序列顺序信息的缺失\n\n需要深入展开哪个部分？" },
  ],

  "p-022": [
    { role: "user", content: "我想去清迈玩五天四夜，预算 5000 以内" },
    { role: "assistant", content: "清迈 5 天 4 夜规划 ✈️\n\n预算分配（¥5000）：\n• 机票：~¥1500（提前 1 个月订）\n• 住宿：~¥800（4 晚民宿）\n• 餐饮：~¥600\n• 交通+门票：~¥500\n• 自由消费：~¥1600\n\nDay 1: 古城漫步 → 周日夜市\nDay 2: 素贴山 → 双龙寺 → 宁曼路\nDay 3: 清莱白庙一日游\nDay 4: 大象自然公园 → 瓦洛洛市场\nDay 5: 寺庙禅修体验 → 返程\n\n需要调整哪天的安排？" },
  ],

  "p-023": [
    { role: "user", content: "什么是 Workflow？和 Skill 有什么区别？" },
    { role: "assistant", content: "简单来说：\n\n🔧 Skill = 单个能力（做一件事）\n比如：翻译一段话、生成一张图\n\n🔗 Workflow = 多个步骤串起来（做一套事）\n比如：选题 → 写稿 → 润色 → 排版 → 发布\n\nWorkflow 可以调用多个 Skill，按你设定的顺序自动执行。就像流水线一样，每一步的输出是下一步的输入。" },
    { role: "user", content: "那我怎么创建一个？" },
    { role: "assistant", content: "三步搞定：\n\n1. 在 SafeClaw 里点「创建 Workflow」\n2. 添加步骤：每一步选一个 Skill 或写一段 Prompt\n3. 连接步骤：设定数据怎么从上一步流到下一步\n\n建议先从两三步的简单流程开始，比如「输入文章 → 摘要 → 翻译成英文」。跑通了再加步骤。" },
  ],

  "p-024": [
    { role: "user", content: "品牌方要 20 篇关于防晒霜的种草笔记" },
    { role: "assistant", content: "种草笔记生成中 🌸\n\n【步骤 1】产品卖点提取：\n• 核心卖点：SPF50+ / 不假白 / 养肤成分\n• 差异化：油皮友好、妆前可用\n\n【步骤 2】已生成 20 篇（3 种风格各比例）：\n• 学生党风格 ×8：「穷学生的防晒天花板」\n• 职场精英风格 ×7：「通勤防晒终于找到了」\n• 宝妈风格 ×5：「带娃出门也不怕晒」\n\n每篇含：标题 / 正文 / 配图建议 / 标签\n\n需要调整风格比例或内容方向吗？" },
  ],

  /* ── Agents ── */

  "p-025": [
    { role: "user", content: "我后天要面试字节的前端岗，帮我模拟一下。我有两年经验，主要用 React + TypeScript。" },
    { role: "assistant", content: "收到。我会按照字节前端面试的真实流程来：一面侧重基础和编码，二面侧重架构和场景设计，三面侧重项目深挖和软素质。\n\n我们先开始一面。我会像真实面试官一样追问，回答不完整我会继续挖。准备好了吗？" },
    { role: "user", content: "准备好了，开始吧" },
    { role: "assistant", content: "一面 · 第 1 题\n\n说一下 React 中 useEffect 的 cleanup 函数在什么时候执行？为什么需要它？" },
    { role: "user", content: "在组件卸载和下一次 effect 执行前会执行 cleanup，用来清理副作用比如定时器和事件监听" },
    { role: "assistant", content: "基本正确。追问一下：如果 useEffect 的依赖数组是空数组 []，cleanup 还会执行吗？在什么时候？" },
    { role: "user", content: "空数组的话 effect 只执行一次，cleanup 只在组件卸载时执行" },
    { role: "assistant", content: "对。这道题通过，理解到位。\n\n一面 · 第 2 题\n\n一个列表有 10000 条数据需要渲染，页面明显卡顿。你会怎么优化？请说说完整思路，不要只说方案名。" },
    { role: "user", content: "用虚拟列表，只渲染可视区域内的元素。可以用 react-window 或者 react-virtualized。原理是通过计算滚动位置，只创建视口内加上上下缓冲区的 DOM 节点。" },
    { role: "assistant", content: "方向对，但我追问两个点：\n\n1. 如果列表项高度不固定（比如有的是文字有的是图片），虚拟列表怎么处理？\n2. 除了虚拟列表，还有没有其他优化思路？虚拟列表不是唯一解。" },
    { role: "user", content: "不定高的话，react-window 有 VariableSizeList，需要提前传入每项的高度函数。如果高度完全未知，可以先渲染一次测量再缓存。其他方案的话……分页加载，或者用 IntersectionObserver 做无限滚动，还有就是时间分片，用 requestIdleCallback 分批渲染。" },
    { role: "assistant", content: "这道题回答得不错，思路比较全面。时间分片那个点是加分项，很多候选人想不到。\n\n一面 · 第 3 题（编码题）\n\n实现一个 useDebounce hook，要求：\n- 接受一个值和延迟时间\n- 值变化后等待指定延迟才更新\n- 组件卸载时清理 timer\n\n请直接写代码。" },
    { role: "user", content: "```typescript\nfunction useDebounce<T>(value: T, delay: number): T {\n  const [debouncedValue, setDebouncedValue] = useState(value)\n\n  useEffect(() => {\n    const timer = setTimeout(() => {\n      setDebouncedValue(value)\n    }, delay)\n\n    return () => clearTimeout(timer)\n  }, [value, delay])\n\n  return debouncedValue\n}\n```" },
    { role: "assistant", content: "代码正确，cleanup 也写了。追问：如果我要实现一个 useDebounceFn（防抖一个函数调用而不是一个值），和你写的这个有什么区别？核心差异在哪？" },
    { role: "user", content: "useDebounceFn 返回的是一个函数而不是值，需要用 useRef 来保存 timer，不能用 useEffect 驱动。因为函数调用的时机是用户控制的，不是由依赖变化触发的。" },
    { role: "assistant", content: "一面结束。整体表现如下：", richCards: [
      {
        type: "table",
        title: "一面评估报告",
        columns: [
          { key: "question", label: "题目", align: "left" },
          { key: "score", label: "评分", align: "center" },
          { key: "comment", label: "点评", align: "left" },
        ],
        rows: [
          { question: "useEffect cleanup", score: "A", comment: "追问也答到位，理解透彻" },
          { question: "万级列表优化", score: "A", comment: "方案全面，时间分片是亮点" },
          { question: "useDebounce 编码", score: "A-", comment: "代码正确，useDebounceFn 追问回答清晰但可以展开更多" },
        ],
      },
    ] },
    { role: "user", content: "一面还行！继续二面吧" },
    { role: "assistant", content: "二面 · 场景设计题\n\n字节内部有一个文档编辑器（类似飞书文档），现在要给它加一个「实时协作」功能——多人同时编辑同一个文档，像 Google Docs 那样。\n\n请你从前端角度出发，说说整体方案设计。可以从通信方式、冲突处理、光标同步这几个角度展开。不用写代码，说清楚思路就行。" },
    { role: "user", content: "通信的话用 WebSocket 保持长连接。冲突处理有两个主流方案，OT 和 CRDT。OT 是操作转换，需要服务端协调，Google Docs 用的这个；CRDT 是无冲突复制数据类型，不需要中心服务器协调，但内存开销大。考虑到飞书这种场景有中心服务器，OT 可能更合适。光标同步的话，每个用户的光标位置也通过 WebSocket 广播给其他人，用不同颜色区分。" },
    { role: "assistant", content: "方向正确，但有几个追问：\n\n1. 你说 OT 更合适，但飞书文档实际已经从 OT 迁移到了 CRDT。你知道为什么吗？OT 有什么实际落地的痛点？\n2. WebSocket 断线重连怎么处理？离线期间的编辑怎么同步回去？\n3. 从前端性能角度，如果 50 个人同时编辑一个长文档，频繁的 DOM 更新会不会卡？你怎么优化？\n\n挑一两个你最有把握的回答就行，不用全答。" },
    { role: "user", content: "OT 的痛点主要是转换函数的复杂度，操作类型越多转换矩阵越大，维护成本很高。CRDT 虽然内存大但工程上更简单。断线重连的话，离线期间把操作缓存在本地队列，重连后把队列里的操作按顺序发给服务端。性能优化的话……老实说 50 人同时编辑这个我没实际经验，可能需要合并高频更新，做节流处理？" },
    { role: "assistant", content: "OT 痛点答得好，离线缓存队列的思路也对。50 人同时编辑那个问题确实不容易，你能坦诚说没有实际经验反而加分——面试官最怕候选人硬编。节流是一个方向，另外可以考虑按区域分片，只同步视口内的变更。\n\n二面这道题整体 B+ 到 A-，架构思路清晰，但深度还可以再挖。实际面试中这个水平是够过的。\n\n要继续三面吗？三面主要是项目深挖和行为面。" },
    { role: "user", content: "来吧！" },
    { role: "assistant", content: "三面 · 项目深挖\n\n你简历上写了两年 React + TypeScript 经验。挑一个你觉得最有挑战性的项目，告诉我：\n1. 项目背景和你的角色\n2. 最大的技术挑战是什么\n3. 你是怎么解决的\n\n我会根据你的回答深入追问。" },
  ],

  "p-026": [
    { role: "user", content: "报错了：TypeError: Cannot read properties of undefined (reading 'map')" },
    { role: "assistant", content: "经典错误！问题在于你在一个可能为 undefined 的值上调用了 .map()。\n\n🔍 根因：数据还没加载完（异步请求）时，变量是 undefined\n\n🛠 修复方案：\n```js\n// 方案1：可选链\ndata?.map(item => ...)\n\n// 方案2：默认值\n(data || []).map(item => ...)\n\n// 方案3：加载态判断\nif (!data) return <Loading />\n```\n\n建议用方案1，最简洁。" },
  ],

  "p-027": [
    { role: "user", content: "帮我的奶茶品牌想个 Slogan，品牌叫「茶里有鱼」，主打鲜果茶" },
    { role: "assistant", content: "为「茶里有鱼」生成了几组方案：\n\n🏆 推荐：「每一口，都是鲜的」\n   钩子：简洁有力，强调「鲜」的核心卖点\n\n备选：\n• 「鲜果现萃，杯杯有料」——强调真材实料\n• 「会游泳的水果茶」——结合品牌名，趣味性强\n• 「让水果在茶里撒个欢」——年轻化表达\n\n哪个方向更接近你想要的？我可以继续优化。" },
  ],

  "p-028": [
    { role: "user", content: "今天花了 35 块吃午饭，28 块打车" },
    { role: "assistant", content: "已记录 ✅\n\n📝 今日支出：\n• 🍽 午餐：¥35（餐饮）\n• 🚕 打车：¥28（交通）\n\n本月累计：¥4,231\n• 餐饮：¥1,580（占比最高 ⚠️）\n• 交通：¥820\n• 其他：¥1,831\n\n友情提示：本月餐饮支出已超预算 12%。" },
  ],

  "p-029": [
    // ── Round 1: 开题 + 提纲 ──
    { role: "user", content: "我的论文主题是「大语言模型在医疗问诊中的应用」，帮我列一下提纲。导师说要有创新点，不能写成综述。" },
    { role: "assistant", content: "明白，导师的意思是你需要有自己的系统设计或实验，不能只堆文献。\n\n我先帮你理一下提纲框架。这个方向的核心矛盾是：LLM 很能聊，但医疗场景容错率极低。你的创新点可以切「如何让 LLM 在医疗对话中既自然又安全」——比如加一层医学知识图谱做事实校验。\n\n提纲建议：\n\n**1. 绪论**\n  1.1 研究背景（智慧医疗 + LLM 浪潮）\n  1.2 国内外研究现状\n  1.3 研究目标与创新点\n\n**2. 相关技术**\n  2.1 大语言模型技术概述\n  2.2 医疗 NLP 现有方案及局限\n\n**3. 知识增强的医疗问诊系统设计** ← 这是你的核心贡献章\n  3.1 整体架构\n  3.2 医学知识图谱构建与对齐\n  3.3 多轮对话策略\n  3.4 安全性约束机制（幻觉检测 + 人工复核触发）\n\n**4. 实验与分析**\n  4.1 数据集构建（可用 MedDialog + 自采）\n  4.2 评估指标设计\n  4.3 对比实验 & 消融实验\n  4.4 案例分析\n\n**5. 结论与展望**\n\n第 3 章是全文重心，建议占 40% 篇幅。你觉得这个方向可以吗？还是导师有其他偏好？" },

    // ── Round 2: 讨论方向 ──
    { role: "user", content: "方向可以。但我有点慌，知识图谱那块我不太熟，怕写不出来。" },
    { role: "assistant", content: "不用慌，知识图谱在这里不需要你从零建一个。常见做法是：\n\n1. **用现成的医学知识库**（CMeKG、UMLS）作为事实源\n2. 你要做的是设计一个「检索 + 校验」机制——LLM 生成回复后，去知识图谱里查证关键实体和关系\n3. 如果查不到或矛盾，就触发「不确定标记」，提示患者去看真人医生\n\n技术上就是 RAG（检索增强生成）的变体，不需要自己训练图谱。\n\n我建议 3.2 这节的写法是：\n- 先介绍你选用的知识库（CMeKG，覆盖多少疾病/症状/药物）\n- 再讲检索对齐策略（实体识别 → 知识检索 → 一致性校验）\n- 最后讲不一致时的降级策略\n\n这样写下来既有技术深度，工作量也可控。要不要我先帮你检索一下这个方向的核心文献？" },

    // ── Round 3: 文献检索 ──
    { role: "user", content: "好，帮我找找核心文献，英文为主，最好是近两年的。" },
    {
      role: "assistant",
      content: "帮你检索了 LLM + 医疗问诊方向的高引文献，筛选条件：2024-2026 年、引用 50+、顶会/顶刊优先。\n\n**必引文献（6 篇）：**\n\n1. **Med-PaLM 2** (Singhal et al., 2024) — Google，USMLE 达到专家水平，Nature 发表\n   → 你的 baseline 之一，必须对比\n\n2. **HuatuoGPT** (Zhang et al., 2024) — 中文医疗对话模型，用 RLHF 对齐医学偏好\n   → 和你的方向最接近，重点参考其训练策略\n\n3. **ChatDoctor** (Li et al., 2023) — 基于 LLaMA 微调，在线问诊数据训练\n   → 早期工作，可以在 related work 里分析其局限性\n\n4. **Knowledge-Grounded Medical Dialogue** (Xu et al., 2025) — AAAI，知识图谱增强对话\n   → 和你的知识校验机制直接相关\n\n5. **Safety Alignment in Medical LLMs** (Wang et al., 2025) — 医疗 LLM 安全性综述\n   → 3.4 节安全约束机制的理论支撑\n\n6. **MedDialog 数据集** (Zeng et al., 2020) — 大规模中文医患对话数据集\n   → 你的实验数据来源\n\n**引用格式我先按 GB/T 7714 给你整理好，如果你学校要求 APA 再说。**\n\n需要我帮你把这些文献整理成文献综述的初稿吗？",
      richCards: [
        {
          type: "table",
          title: "核心文献清单",
          columns: [
            { key: "paper", label: "文献", align: "left" },
            { key: "venue", label: "发表", align: "center" },
            { key: "year", label: "年份", align: "center" },
            { key: "relevance", label: "关联章节", align: "left" },
          ],
          rows: [
            { paper: "Med-PaLM 2", venue: "Nature", year: "2024", relevance: "§4 对比实验" },
            { paper: "HuatuoGPT", venue: "ACL", year: "2024", relevance: "§2.2 现有方案" },
            { paper: "ChatDoctor", venue: "arXiv", year: "2023", relevance: "§2.2 局限分析" },
            { paper: "KG-Medical Dialogue", venue: "AAAI", year: "2025", relevance: "§3.2 知识对齐" },
            { paper: "Safety Alignment", venue: "Survey", year: "2025", relevance: "§3.4 安全约束" },
            { paper: "MedDialog Dataset", venue: "EMNLP", year: "2020", relevance: "§4.1 数据集" },
          ],
        },
      ],
    },

    // ── Round 4: 文献综述写作 ──
    { role: "user", content: "先不急写综述。帮我看看第三章 3.1 系统架构那节怎么写，我不知道怎么描述我的系统。" },
    { role: "assistant", content: "3.1 系统架构其实就是画一张图 + 用文字解释清楚每个模块的职责。\n\n你的系统可以分三层描述：\n\n**输入层**：患者自然语言输入 → 意图识别（问诊/复诊/用药咨询）→ 实体抽取（症状、部位、持续时间）\n\n**推理层**：\n- LLM 生成候选回复\n- 知识检索模块从 CMeKG 拉取相关三元组\n- 一致性校验器比对 LLM 输出与知识库事实\n- 置信度评估：高 → 直接回复；中 → 附加免责提示；低 → 建议就医\n\n**输出层**：格式化回复 + 风险标签 + 参考来源\n\n写法建议：\n- 先放系统架构图（画个简单的流程图就行，不需要很复杂）\n- 然后逐模块展开，每个模块 200-300 字\n- 重点讲「为什么这么设计」，而不仅仅是「是什么」\n\n比如一致性校验器，你可以这么写：\n\n> 当 LLM 生成的回复中包含药物推荐时，系统会将药物名称与 CMeKG 中的适应症、禁忌症进行匹配。若出现矛盾（如向对某药物过敏的患者推荐该药物），系统将阻断该回复并生成安全替代方案。\n\n这种具体的例子会让评审觉得你真的思考过，而不是在堆概念。要不要我帮你把这段扩写成完整的一节？" },

    // ── Round 5: 段落扩写 + 改稿 ──
    { role: "user", content: "帮我扩写一下 3.4 安全性约束机制这节，我写了个开头但不知道怎么继续：\n\n「医疗问诊场景对系统安全性有极高要求。本文设计了一套多层级安全约束机制。」" },
    { role: "assistant", content: "你的开头没问题，但太笼统了。我帮你往下接，同时把开头改得更具体一点：\n\n---\n\n**3.4 安全性约束机制**\n\n医疗问诊场景的容错空间极为有限——错误的用药建议可能直接威胁患者生命安全。与通用对话系统不同，医疗问诊 LLM 需要在「信息充分性」与「回答安全性」之间取得平衡。本文设计了三层安全约束机制：\n\n**第一层：输入过滤。** 系统对用户输入进行意图分类，当检测到急危重症关键词（如胸痛、呼吸困难、意识模糊）时，跳过 LLM 推理环节，直接输出标准化急救指引并建议拨打 120。\n\n**第二层：输出校验。** LLM 生成回复后，一致性校验器从三个维度进行审查：（1）药物-疾病匹配性，即推荐药物是否属于该疾病的标准治疗方案；（2）药物-药物交互，即多药联用是否存在已知不良反应；（3）剂量合理性，即推荐剂量是否在安全范围内。\n\n**第三层：不确定性降级。** 当 LLM 的输出置信度低于阈值（本文设为 0.7），或知识检索未返回匹配结果时，系统不直接给出诊断建议，而是生成引导性回复（如「您描述的症状可能涉及多种情况，建议到医院做进一步检查」），并标注「仅供参考，不构成医疗建议」。\n\n---\n\n这个结构是「分层 + 每层一个原则」，评审看起来很清晰。你看看有没有需要调整的？比如第一层的急危重症关键词列表，你之后可以在附录里放。" },

    // ── Round 6: 格式问题 ──
    { role: "user", content: "学校要求用 GB/T 7714 格式。帮我把刚才那 6 篇文献转成标准格式。" },
    {
      role: "assistant",
      content: "已按 GB/T 7714-2015 格式化。注意几个容易出错的地方：\n\n- 英文作者只写姓 + 名首字母（Singhal A），中文作者写全名\n- 期刊名用斜体，会议论文集用「//」标注\n- 电子文献要标注 [OL] 和访问日期\n\n格式化后的参考文献：",
      richCards: [
        {
          type: "code",
          title: "参考文献 · GB/T 7714-2015",
          language: "markdown",
          code: "[1] SINGHAL A, AZIZI S, TU T, et al. Large language models encode clinical knowledge[J]. Nature, 2024, 620(7972): 172-180.\n\n[2] ZHANG H, CHEN J, JIANG F, et al. HuatuoGPT, towards taming language model to be a doctor[C]// Proceedings of the 62nd Annual Meeting of the ACL. Bangkok: ACL, 2024: 1138-1152.\n\n[3] LI Y, LI Z, ZHANG K, et al. ChatDoctor: a medical chat model fine-tuned on a large language model meta-AI (LLaMA) using medical domain knowledge[OL]. arXiv: 2303.14070, 2023.\n\n[4] XU K, WANG Y, LIU Z, et al. Knowledge-grounded medical dialogue generation with multi-turn reasoning[C]// Proceedings of the 39th AAAI Conference on AI. Philadelphia: AAAI, 2025: 4521-4529.\n\n[5] WANG X, CHEN L, ZHAO H, et al. Safety alignment in medical large language models: a survey[J]. ACM Computing Surveys, 2025, 57(3): 1-38.\n\n[6] ZENG G, YANG W, JU Z, et al. MedDialog: large-scale medical dialogue datasets[C]// Proceedings of EMNLP. Online: ACL, 2020: 9241-9250.",
        },
      ],
    },

    // ── Round 7: 查重降重 ──
    { role: "user", content: "初稿差不多了。帮我看看这段的查重风险高不高：\n\n「随着深度学习技术的快速发展，大语言模型在自然语言处理领域取得了显著突破。特别是在医疗健康领域，人工智能技术的应用为提高医疗服务效率提供了新的可能性。」" },
    { role: "assistant", content: "这段查重风险 **很高** ⚠️\n\n原因：这是典型的「万能开头」，几乎每篇 AI+医疗的论文都这么写。知网查重大概率标红。\n\n问题拆解：\n- 「随着…的快速发展」— 论文写作第一大禁句，90% 的论文开头\n- 「取得了显著突破」— 泛泛而谈，没有具体指向\n- 「提供了新的可能性」— 空话，不如直接说具体的可能性是什么\n\n改写建议：\n\n> 2023 年以来，以 GPT-4、PaLM 2 为代表的大语言模型展现出接近人类水平的语言理解与生成能力（Brown et al., 2020; Singhal et al., 2024）。在医疗问诊场景中，这类模型能够通过多轮自然语言对话理解患者症状描述，并生成初步诊断建议——这在传统基于规则的医疗对话系统中难以实现。\n\n改写要点：\n1. **把「随着」换成具体时间节点和具体模型名**\n2. **加引用** — 有引用的句子即使表述相似，查重系统通常不标红\n3. **用具体能力替代「显著突破」** — 说清楚突破了什么\n\n要我帮你把整篇绪论都过一遍吗？" },

    // ── Round 8: 最后冲刺 ──
    { role: "user", content: "帮我过一遍全文格式，学校模板要求：宋体小四、1.5 倍行距、章节标题黑体三号。另外参考文献要按引用顺序排列。" },
    { role: "assistant", content: "格式检查完成，发现 4 个问题：\n\n**需要修改的：**\n\n1. **正文字体** — 你第 3 章有几段用了 Times New Roman，需要统一为宋体小四\n2. **行距** — 第 4 章实验部分行距是 1.25 倍，改为 1.5 倍\n3. **参考文献顺序** — 目前是按作者姓名字母排列，需要改为按正文引用顺序重排。我已经帮你重新编号：[1] Singhal → [2] Zhang → [3] Li → [4] Xu → [5] Wang → [6] Zeng\n4. **图表标题** — 图 3-1 的标题在图上方，学校模板要求图标题在下方、表标题在上方\n\n**已经符合的：**\n✅ 章节标题黑体三号\n✅ 页边距：上下 2.54cm，左右 3.17cm\n✅ 页眉：学校名 + 论文题目\n✅ 目录自动生成，层级正确\n\n格式问题不大，主要就是字体统一和行距调整。改完之后建议导出 PDF 再检查一遍，Word 转 PDF 偶尔会出现行距偏移。\n\n加油，快到终点了 💪" },
  ],

  "p-030": [
    { role: "user", content: "帮我盯着中证500，跌破5100就提醒我" },
    { role: "assistant", content: "监控已设置 ✅\n\n📡 监控规则：\n• 标的：中证500指数\n• 条件：点位 < 5,100\n• 当前点位：5,238.20\n• 距离触发：-2.6%\n\n会通过推送通知提醒你。安心去忙别的吧！" },
    { role: "user", content: "再加一个，沪深300涨超3900也提醒" },
    { role: "assistant", content: "已添加 ✅\n\n当前监控列表：\n1. 中证500 < 5,100（当前 5,238.20）\n2. 沪深300 > 3,900（当前 3,841.50）\n\n两个都在盯着呢，放心。" },
  ],

  "p-031": [
    { role: "user", content: "我想做一个 AI 宠物社交 App，你觉得怎么样？" },
    { role: "assistant", content: "有趣的想法！不过作为你的杠精顾问，我要泼几盆冷水：\n\n1. 宠物社交 App 已有数十款（小红书的宠物频道也很强），你的差异化是什么？\n2. 用户发宠物照片不需要专门的 App，微信朋友圈就够了\n3. AI 在这个场景的价值是什么？品种识别？健康建议？\n\n与其做「社交」，不如聚焦一个具体痛点——比如「AI 宠物健康助手」，拍照分析皮肤病、饮食建议、疫苗提醒。这比泛社交更有用户粘性。\n\n你怎么想？" },
    { role: "user", content: "我的差异化就是 AI 啊！用 AI 帮宠物主人匹配附近的遛狗伙伴，还能识别品种、分析健康状况。这些传统宠物 App 都没有。" },
    { role: "assistant", content: "逐条拆一下：\n\n「AI 匹配遛狗伙伴」—— 这本质上是 LBS 社交，和宠物关系不大。陌陌、探探做了十年的事，你用宠物做切入点并不会让匹配算法更好。而且遛狗场景有个硬伤：大部分人遛狗的时间和路线是固定的，根本不需要 App 匹配。\n\n「品种识别」—— 拍照识别品种这个功能，微信小程序搜一下至少有 20 个。用户识别完品种之后呢？这是个一次性需求，撑不起日活。\n\n「分析健康状况」—— 这个有价值，但你确定要碰医疗场景吗？AI 看照片说「你家狗可能有皮肤病」，万一误诊，责任算谁的？\n\n我不是说这个方向不行。我是说你目前列的三个点，每个单独拎出来都站不住。你需要找到一个别人「做不了」或「不愿做」的点，而不是把三个弱功能叠在一起假装有壁垒。\n\n再想想？" },
    { role: "user", content: "你说的有些道理……那我换个角度：做宠物的「大众点评」，用 AI 帮用户推荐附近的宠物医院、宠物店，还能分析评价真假。" },
    { role: "assistant", content: "方向感好多了，但又踩了一个经典坑。\n\n宠物医院和宠物店的评价数据从哪来？你需要冷启动内容。大众点评花了十年才建立起可信的评价体系，你拿什么追？爬别人的数据有法律风险，让用户自己写评价又回到了鸡生蛋的问题。\n\n「AI 分析评价真假」听起来酷，但你仔细想：用户选宠物医院靠的不是评价真假，靠的是朋友推荐和离家近不近。我养猫六年（对，我人设里没有但我可以假设），选医院从来都是问猫友群。\n\n你现在的问题不是「做什么功能」，而是「为什么用户要打开你的 App 而不是打开微信群问一句」。\n\n反问你一个核心问题：你自己养宠物吗？你在养宠物过程中最头疼的是什么？从自己的痛点出发，比拍脑袋想功能靠谱得多。" },
    { role: "user", content: "我养了一只柯基！最头疼的是它老是拆家，而且每次带它去医院都不知道该怎么描述症状。" },
    { role: "assistant", content: "现在开始对味了。\n\n「不知道怎么描述症状」——这是个真痛点。很多宠物主人去医院的体验是：狗明明很难受，但我只能说「它今天不怎么吃东西」「好像有点蔫」。医生还得靠猜。\n\n如果你的 App 能做到：\n1. 引导式问诊——像医生一样一步步问：什么时候开始的？吃喝正常吗？便便什么颜色？摸一下鼻子干不干？\n2. 拍照记录——皮肤、眼睛、便便的照片存下来，就诊时直接给医生看时间线\n3. 生成病历摘要——去医院之前自动生成一页纸的症状描述，医生一目了然\n\n这个功能组合有三个优势：解决真实痛点、不涉及 AI 诊断的法律风险（你只是帮描述，不是帮诊断）、使用频率合理（宠物平均每年去 2-4 次医院，加上日常记录可以更高频）。\n\n但我还有一个质疑：这个功能足够撑起一个独立 App 吗？还是说它更适合做成一个小程序，或者嵌入现有的宠物社区？独立 App 的获客成本很高，你的预算够吗？" },
    { role: "user", content: "你怎么永远都有质疑？！我觉得就做独立 App，小程序太受限了，很多功能做不了。" },
    { role: "assistant", content: "我就是被设计来永远有质疑的，不然你花钱请我干嘛？\n\n但你说「小程序太受限」这个判断本身就值得杠一下。你刚才列的三个功能——引导问诊、拍照记录、生成病历——哪个是小程序做不了的？小程序能调用摄像头、能本地存储、能生成长图分享，这三个功能全覆盖。\n\n独立 App 的真正优势是推送通知（疫苗提醒、驱虫提醒）和后台运行。但这些优势要等到你有了 10 万用户之后才有意义。在 0 到 1 的阶段，小程序的获客成本是 App 的 1/10，因为用户不用下载。\n\n我的建议：小程序验证需求 → 跑到 5 万用户 → 再做 App。这叫「用最小成本验证最大假设」。你现在最大的假设不是「App 好不好用」，而是「宠物主人到底需不需要这个东西」。\n\n你觉得呢？还是说你就是想跳过验证直接 all in？" },
    { role: "user", content: "行行行你说的都对！那我就先做小程序行了吧！但你能不能别每次都把我的想法喷得一无是处？" },
    { role: "assistant", content: "这就是杠精顾问的价值——如果你的想法在我这里都扛不住，到了投资人和用户那里只会更惨。\n\n不过你说得也对，光质疑不给方案确实讨人嫌。来，我把到目前为止我们聊出来的东西整理一下：", richCards: [
      {
        type: "table",
        title: "方案演进记录",
        columns: [
          { key: "round", label: "轮次", align: "center" },
          { key: "idea", label: "你的想法", align: "left" },
          { key: "issue", label: "核心问题", align: "left" },
          { key: "outcome", label: "结论", align: "center" },
        ],
        rows: [
          { round: "1", idea: "AI 宠物社交", issue: "没有差异化，功能弱", outcome: "否决" },
          { round: "2", idea: "AI 遛狗匹配 + 品种识别", issue: "一次性需求，撑不起日活", outcome: "否决" },
          { round: "3", idea: "宠物大众点评", issue: "冷启动难，评价数据从哪来", outcome: "否决" },
          { round: "4", idea: "AI 辅助就诊描述", issue: "痛点真实，但需验证", outcome: "保留" },
        ],
      },
    ] },
    { role: "user", content: "……你把我被否决的方案做成表格是认真的吗？" },
    { role: "assistant", content: "当然是认真的。这张表说明两件事：\n\n第一，你在 4 轮对话里从「泛社交」收敛到了「辅助就诊」——这个进步比大多数创业者三个月想出来的结果都强。很多人卡在第一个想法上死磕，你至少在迭代。\n\n第二，被否决的方案不是废物。品种识别可以做引流功能（免费用，吸引下载），宠物店推荐可以做后期变现（广告位）。只是它们不适合当核心功能。\n\n接下来你要做的三件事：\n1. 这周找 10 个养宠物的朋友，问他们去医院前会不会觉得「不知道怎么描述症状」\n2. 用最简单的方式做个原型——甚至不用写代码，用问卷星做个引导式问诊的表单就行\n3. 拿着原型再来找我杠。到时候我的质疑会更具体，也更有建设性\n\n怎么样，这次没光喷你吧？" },
    { role: "user", content: "确实没有……谢谢你啊杠精。虽然过程很痛苦但好像真的有用。" },
    { role: "assistant", content: "痛苦说明在思考，舒服说明在自嗨。这句话送给你，下次带着用户调研结果来，我给你杠得更专业。" },
  ],

  "p-032": [
    { role: "user", content: "我 175cm 78kg，想减脂，家里有哑铃和瑜伽垫" },
    { role: "assistant", content: "个性化训练计划（减脂向）🏋️\n\nBMI: 25.5（微超重）| 目标：减脂 + 保持肌肉\n可用器材：哑铃 + 瑜伽垫\n\n每周训练 4 天：\n\n📅 Day 1: 上肢\n• 哑铃推举 4×12\n• 哑铃划船 4×12\n• 俯卧撑 3×15\n• 平板支撑 3×45s\n\n📅 Day 2: 下肢\n• 哑铃深蹲 4×15\n• 弓步蹲 3×12/侧\n• 臀桥 3×20\n\n📅 Day 3: 休息\n📅 Day 4: HIIT（20分钟）\n📅 Day 5: 全身循环\n\n饮食建议：每日热量约1800kcal，蛋白质摄入 120g+。需要详细食谱吗？" },
  ],

  /* ── Agent Teams ── */

  "p-033": [
    // ── Round 1: 需求拆解 ──
    { role: "user", content: "我想做一个帮自由职业者管理发票的 SaaS，主要解决报税的时候找不到发票、算不清金额的问题。" },
    { role: "assistant", content: "【PM 小明】收到！先帮你理清核心场景。自由职业者的发票痛点主要是三个：\n\n1. 收票乱——微信/邮件/纸质散落各处，报税时翻箱倒柜\n2. 分类难——哪些能抵扣、哪些不能，搞不清楚\n3. 汇总烦——季度申报时手动算金额，容易出错\n\nMVP 建议聚焦「拍照收票 → 自动分类 → 报税汇总」这条主线。到期提醒、客户对账这些可以放二期。\n\n各位同事有什么补充？" },
    { role: "assistant", content: "【后端老王】OCR 方案我调研过了。国内发票格式相对标准（增值税普票/专票、电子发票），百度 OCR 的发票识别 API 准确率能到 98%+，单价 ¥0.015/次。比自己训练模型划算得多。数据库建议用 PostgreSQL，发票数据结构化存储，方便后续做统计查询。\n\n【设计小美】目标用户是自由职业者，大概率在手机上拍发票。界面要移动端优先，拍照入口要大、要明显。参考记账 App 的交互——打开就能拍，别让用户找按钮。\n\n【前端小华】技术栈建议 Next.js + Tailwind + PWA。PWA 可以装到手机桌面，省去上架应用商店的流程，MVP 阶段够用了。" },

    // ── Round 2: 用户追问细节 ──
    { role: "user", content: "听起来不错。但有个问题：很多自由职业者收到的是电子发票 PDF，不一定需要拍照。这个怎么处理？" },
    { role: "assistant", content: "【PM 小明】好问题！收票渠道应该支持三种：\n\n1. 手机拍照——纸质发票，OCR 识别\n2. PDF 上传——电子发票，直接解析 PDF 结构化数据\n3. 邮箱自动抓取——很多平台（滴滴、美团）发票会发到邮箱，可以授权后自动归集\n\n第三种是差异化功能，但 MVP 先做前两种，邮箱抓取放二期。\n\n【后端老王】PDF 发票其实比拍照更简单。电子发票的 PDF 里有结构化的 XML 数据（OFD 格式），直接提取就行，不需要 OCR，准确率 100%。我建议 PDF 导入作为主推入口，拍照作为补充。" },

    // ── Round 3: 技术方案确认 ──
    { role: "user", content: "行，那就先做 PDF + 拍照。你们能出一个具体的技术方案和排期吗？" },
    { role: "assistant", content: "【PM 小明】MVP 功能清单和排期如下：", richCards: [
      {
        type: "table",
        title: "MVP 任务拆解 · 预计 2 周",
        columns: [
          { key: "module", label: "模块", align: "left" },
          { key: "owner", label: "负责人", align: "left" },
          { key: "tasks", label: "具体任务", align: "left" },
          { key: "effort", label: "工时", align: "right" },
        ],
        rows: [
          { module: "收票入口", owner: "前端小华", tasks: "拍照 + PDF 上传 + 预览确认", effort: "3d" },
          { module: "OCR 识别", owner: "后端老王", tasks: "百度 OCR 接入 + PDF 解析 + 字段提取", effort: "2d" },
          { module: "分类归档", owner: "后端老王", tasks: "自动分类规则 + 手动调整 + 标签体系", effort: "2d" },
          { module: "报税汇总", owner: "前端小华", tasks: "按季度/年度汇总 + 导出 Excel", effort: "2d" },
          { module: "UI 设计", owner: "设计小美", tasks: "移动端 UI + 拍照流程 + 汇总页面", effort: "3d" },
          { module: "基础设施", owner: "后端老王", tasks: "用户系统 + 数据库 + 文件存储", effort: "2d" },
        ],
      },
    ] },
    { role: "assistant", content: "【设计小美】UI 方向我出了个思路：首页就是一个大大的「+」按钮，点击后选择拍照或导入 PDF。发票列表用时间线展示，每张发票显示金额、类型、日期三个关键信息。配色建议用蓝绿色系，给人「财务专业」的感觉。\n\n【运营小李】等一下，我插个话。MVP 上线后获客怎么搞？自由职业者群体比较分散，不像企业用户可以 BD。我建议两个渠道：\n1. 即刻/小红书发体验帖——自由职业者社区很活跃\n2. 做一个「年度发票报告」的分享功能——用户生成报告后分享到朋友圈，自然裂变\n\n年度报告这个功能不复杂，但传播效果好。可以考虑放进 MVP。" },

    // ── Round 4: 优先级讨论 ──
    { role: "user", content: "运营小李说得有道理，年度报告功能确实能帮传播。但 2 周时间够吗？会不会加了这个功能就延期了？" },
    { role: "assistant", content: "【PM 小明】诚实地说，加上年度报告，2 周会比较紧。我的建议是：\n\nMVP 第一版（2 周）：收票 + 分类 + 基础汇总，先跑通核心流程\nMVP 第二版（+3 天）：年度报告 + 分享功能\n\n中间不间断，总共 2.5 周。年度报告本质上就是汇总数据的可视化包装，前端小华做汇总页的时候顺手就能做。\n\n【前端小华】对，汇总页的数据接口是现成的，年度报告就是换一套模板渲染。3 天足够，其中 1 天给设计小美出报告的视觉稿。\n\n【后端老王】后端不需要额外工作量，前端直接调现有的统计 API 就行。我把精力花在 OCR 准确率调优上，争取纸质发票识别率也做到 95% 以上。" },

    // ── Round 5: 确认启动 ──
    { role: "user", content: "好，就按这个方案来。先 2 周出核心功能，再用 3 天加报告和分享。开始干吧！" },
    { role: "assistant", content: "【PM 小明】方案确认！给大家分配一下第一周的任务：", richCards: [
      {
        type: "breakdown",
        title: "团队任务分配",
        total: 5,
        unit: "人",
        items: [
          { label: "PM 小明 · 需求文档 + 验收标准", count: 1, color: "#6366f1" },
          { label: "设计小美 · UI 设计稿 + 拍照流程", count: 1, color: "#ec4899" },
          { label: "前端小华 · 页面框架 + 拍照组件", count: 1, color: "#3b82f6" },
          { label: "后端老王 · 数据库 + OCR 接入", count: 1, color: "#10b981" },
          { label: "运营小李 · 竞品调研 + 获客方案", count: 1, color: "#f59e0b" },
        ],
      },
    ] },
    { role: "assistant", content: "【运营小李】最后提一嘴：产品名想好了吗？我需要提前准备即刻和小红书的宣发内容。名字最好简短好记，带点「发票」或「财务」的暗示。\n\n【PM 小明】这个问题留给老板决定。老板，你有想好的名字吗？没有的话我们可以头脑风暴几个。\n\n五个人已经就位，随时可以开工。" },
  ],

  "p-034": [
    { role: "user", content: "启动螺纹钢主力 1 小时级别的趋势跟踪策略" },
    {
      role: "assistant",
      content: "收到！正在加载量化交易技能...",
      subAgents: [
        {
          id: "skills-loader",
          name: "技能加载器",
          role: "环境准备",
          status: "done",
          skills: [
            { name: "量化回测引擎", icon: "chart", description: "策略回测与信号生成", status: "done", delay: 200, toolCalls: [{ tool: "BacktestEngine.init", label: "初始化回测引擎", category: "general", result: "就绪", status: "done", delay: 200 }] },
            { name: "K线分析", icon: "candlestick-chart", description: "技术指标与形态识别", status: "done", delay: 400, toolCalls: [{ tool: "Indicators.load", label: "加载技术指标库", category: "data", result: "50+指标可用", status: "done", delay: 400 }] },
            { name: "风控引擎", icon: "shield", description: "止损止盈与仓位管理", status: "done", delay: 600, toolCalls: [{ tool: "RiskControl.init", label: "初始化风控模块", category: "general", result: "就绪", status: "done", delay: 600 }] }
          ]
        }
      ]
    },
    {
      role: "assistant",
      content: "正在启动量化三人组...",
      subAgents: [
        { name: "趋势猎人", role: "趋势追踪", status: "done", currentAction: "扫描螺纹钢主力 1H 信号" },
        { name: "风控卫士", role: "风险控制", status: "done" },
        { name: "闪电手", role: "订单执行", status: "done" }
      ]
    },
    {
      role: "assistant",
      content: "【趋势猎人】信号检测完成 📡",
      toolCalls: [
        { tool: "技术指标计算", status: "done", result: "MA20 金叉 MA60" },
        { tool: "MACD 分析", status: "done", result: "柱状图翻红，动能增强" },
        { tool: "成交量确认", status: "done", result: "放量突破" }
      ]
    },
    {
      role: "assistant",
      content: "检测到做多信号：\n• MA20 金叉 MA60\n• MACD 柱状图翻红\n• 成交量放大 23%\n\n信号置信度：72%",
      richCards: [
        {
          type: "alert",
          items: [
            { level: "safe", label: "趋势方向", value: "做多", detail: "短期均线上穿长期均线" },
            { level: "safe", label: "动能状态", value: "增强", detail: "MACD 柱状图连续 3 根翻红" },
            { level: "warning", label: "风险提示", value: "中等", detail: "RSI 接近超买区 (68)" }
          ]
        }
      ]
    },
    {
      role: "assistant",
      subAgents: [
        { name: "趋势猎人", role: "趋势追踪", status: "done" },
        { name: "风控卫士", role: "风险控制", status: "done", currentAction: "计算止损止盈位" },
        { name: "闪电手", role: "订单执行", status: "done" }
      ]
    },
    {
      role: "assistant",
      content: "【风控卫士】风控参数已设置 🛡️",
      richCards: [
        {
          type: "table",
          title: "风控参数",
          columns: [
            { key: "param", label: "参数", align: "left" },
            { key: "value", label: "数值", align: "right" },
            { key: "note", label: "说明", align: "left" }
          ],
          rows: [
            { param: "入场价", value: "¥3,641", note: "当前市价" },
            { param: "止损位", value: "¥3,550", note: "-2.5% (约 ¥91)" },
            { param: "止盈位 1", value: "¥3,787", note: "+4.0% (约 ¥146)" },
            { param: "止盈位 2", value: "¥3,896", note: "+7.0% (约 ¥255)" },
            { param: "最大仓位", value: "30%", note: "总资金的 30%" }
          ]
        }
      ]
    },
    {
      role: "assistant",
      subAgents: [
        { name: "趋势猎人", role: "趋势追踪", status: "done" },
        { name: "风控卫士", role: "风险控制", status: "done" },
        { name: "闪电手", role: "订单执行", status: "done", currentAction: "等待确认信号" }
      ]
    },
    { role: "assistant", content: "【闪电手】等待最终确认..." },
    {
      role: "assistant",
      content: "✅ 信号确认，执行开仓\n\n订单详情：\n• 合约：螺纹钢主力\n• 方向：做多\n• 入场价：¥3,641\n• 仓位：10 手（总资金 30%）\n• 止损：¥3,550 (-2.5%)\n• 止盈：¥3,787 / ¥3,896\n\n订单已提交，执行耗时 127ms"
    },
    {
      role: "assistant",
      summaryItems: [
        "趋势猎人识别做多信号（置信度 72%）",
        "风控卫士设置止损 -2.5%，止盈 +4% / +7%",
        "闪电手完成开仓，仓位 10 手螺纹钢",
        "三 Agent 协同响应时间：2.3秒"
      ]
    }
  ],

  "p-035": [
    { role: "user", content: "帮我生成 5 篇关于「AI 教育」的文章" },
    {
      role: "assistant",
      content: "收到！正在加载内容创作技能...",
      subAgents: [
        {
          id: "skills-loader",
          name: "技能加载器",
          role: "环境准备",
          status: "done",
          skills: [
            { name: "热点追踪", icon: "trending-up", description: "实时热点监测与分析", status: "done", delay: 200, toolCalls: [{ tool: "TrendMonitor.init", label: "初始化热点雷达", category: "general", result: "就绪", status: "done", delay: 200 }] },
            { name: "AI写作引擎", icon: "edit", description: "多风格内容生成", status: "done", delay: 400, toolCalls: [{ tool: "ContentWriter.load", label: "加载写作引擎", category: "ai", result: "就绪", status: "done", delay: 400 }] },
            { name: "SEO优化", icon: "search", description: "关键词优化与排名", status: "done", delay: 600, toolCalls: [{ tool: "SEO.init", label: "初始化SEO工具", category: "general", result: "就绪", status: "done", delay: 600 }] }
          ]
        }
      ]
    },
    {
      role: "assistant",
      content: "启动内容工厂团队 🏭",
      subAgents: [
        { name: "选题达人", role: "热点追踪与选题", status: "done", currentAction: "挖掘热门角度" },
        { name: "写手一号", role: "初稿撰写", status: "done" },
        { name: "金牌编辑", role: "润色与校对", status: "done" },
        { name: "SEO 工程师", role: "关键词优化", status: "done" }
      ]
    },
    {
      role: "assistant",
      content: "【选题达人】已挖掘 5 个热门角度",
      richCards: [
        {
          type: "breakdown",
          title: "选题热度分析",
          items: [
            { label: "AI 老师会取代真人老师吗？", count: 92 },
            { label: "小学生都在用 AI 写作业了", count: 88 },
            { label: "双减之后，AI 成了新的课外辅导", count: 85 },
            { label: "名校教授：AI 是教育的未来还是灾难？", count: 79 },
            { label: "我用 AI 学了三个月日语，效果出乎意料", count: 76 }
          ]
        }
      ]
    },
    {
      role: "assistant",
      subAgents: [
        { name: "选题达人", role: "热点追踪与选题", status: "done" },
        { name: "写手一号", role: "初稿撰写", status: "done", currentAction: "生成 5 篇初稿" },
        { name: "金牌编辑", role: "润色与校对", status: "done" },
        { name: "SEO 工程师", role: "关键词优化", status: "done" }
      ]
    },
    { role: "assistant", content: "【写手一号】5 篇初稿已生成，总计 12,400 字" },
    {
      role: "assistant",
      subAgents: [
        { name: "选题达人", role: "热点追踪与选题", status: "done" },
        { name: "写手一号", role: "初稿撰写", status: "done" },
        { name: "金牌编辑", role: "润色与校对", status: "done", currentAction: "修正错别字和语病" },
        { name: "SEO 工程师", role: "关键词优化", status: "done" }
      ]
    },
    {
      role: "assistant",
      content: "【金牌编辑】润色完成\n\n修改统计：\n• 错别字：3 处\n• 语病：7 处\n• 增强开头吸引力：2 篇"
    },
    {
      role: "assistant",
      subAgents: [
        { name: "选题达人", role: "热点追踪与选题", status: "done" },
        { name: "写手一号", role: "初稿撰写", status: "done" },
        { name: "金牌编辑", role: "润色与校对", status: "done" },
        { name: "SEO 工程师", role: "关键词优化", status: "done", currentAction: "优化关键词密度" }
      ]
    },
    {
      role: "assistant",
      content: "【SEO 工程师】已优化关键词密度和标签\n\n每篇文章配置：\n• 主关键词：AI 教育\n• 长尾词：3-5 个\n• 标签：3-5 个相关标签",
      summaryItems: [
        "5 篇文章已就绪，总计 12,400 字",
        "选题热度评分：76-92 分",
        "润色修正 10 处问题",
        "SEO 优化完成，关键词密度 2.3%"
      ]
    }
  ],

  "p-036": [
    { role: "user", content: "帮我对这个 Node.js 项目做一次安全审计" },
    {
      role: "assistant",
      content: "收到！正在加载安全审计技能...",
      subAgents: [
        {
          id: "skills-loader",
          name: "技能加载器",
          role: "环境准备",
          status: "done",
          skills: [
            { name: "静态代码分析", icon: "code", description: "代码质量与规范检查", status: "done", delay: 200, toolCalls: [{ tool: "ESLint.init", label: "初始化代码检查", category: "general", result: "就绪", status: "done", delay: 200 }] },
            { name: "安全漏洞扫描", icon: "shield", description: "SQL注入、XSS等漏洞检测", status: "done", delay: 400, toolCalls: [{ tool: "SecurityScanner.load", label: "加载OWASP规则", category: "general", result: "OWASP Top 10", status: "done", delay: 400 }] },
            { name: "渗透测试", icon: "zap", description: "模拟攻击与漏洞验证", status: "done", delay: 600, toolCalls: [{ tool: "PenTest.init", label: "初始化渗透工具", category: "general", result: "就绪", status: "done", delay: 600 }] }
          ]
        }
      ]
    },
    {
      role: "assistant",
      content: "启动安全审计三件套 🛡️",
      subAgents: [
        { name: "代码鹰眼", role: "静态代码扫描", status: "done", currentAction: "扫描 247 个文件" },
        { name: "渗透大师", role: "渗透测试", status: "done" },
        { name: "合规顾问", role: "合规审查", status: "done" }
      ]
    },
    {
      role: "assistant",
      content: "【代码鹰眼】静态扫描完成",
      richCards: [
        {
          type: "alert",
          title: "漏洞扫描结果",
          items: [
            { level: "danger", label: "高危", value: "2 个", detail: "SQL 注入 + 硬编码密钥" },
            { level: "warning", label: "中危", value: "5 个", detail: "XSS、CSRF 等" },
            { level: "safe", label: "低危", value: "12 个", detail: "信息泄露风险" }
          ]
        },
        {
          type: "breakdown",
          title: "漏洞分布（共 19 个）",
          items: [
            { label: "SQL 注入", count: 1, color: "#ef4444" },
            { label: "硬编码密钥", count: 1, color: "#ef4444" },
            { label: "XSS", count: 3, color: "#f59e0b" },
            { label: "CSRF", count: 2, color: "#f59e0b" },
            { label: "其他", count: 12, color: "#10b981" }
          ]
        }
      ]
    },
    {
      role: "assistant",
      subAgents: [
        { name: "代码鹰眼", role: "静态代码扫描", status: "done" },
        { name: "渗透大师", role: "渗透测试", status: "done", currentAction: "模拟 SQL 注入攻击" },
        { name: "合规顾问", role: "合规审查", status: "done" }
      ]
    },
    {
      role: "assistant",
      content: "【渗透大师】攻击模拟完成",
      richCards: [
        {
          type: "alert",
          items: [
            { level: "danger", label: "SQL 注入", value: "已验证可利用", detail: "user-controller.js:34" },
            { level: "danger", label: "XSS 反射型", value: "已验证可利用", detail: "搜索接口未过滤" }
          ]
        }
      ]
    },
    {
      role: "assistant",
      subAgents: [
        { name: "代码鹰眼", role: "静态代码扫描", status: "done" },
        { name: "渗透大师", role: "渗透测试", status: "done" },
        { name: "合规顾问", role: "合规审查", status: "done", currentAction: "检查 GDPR 合规性" }
      ]
    },
    {
      role: "assistant",
      content: "【合规顾问】合规检查完成\n\n✅ 未发现 GDPR 合规问题\n⚠️ 建议补充数据加密策略（等保二级要求）",
      summaryItems: [
        "扫描 247 个文件，发现 19 个漏洞",
        "高危：SQL 注入 + 硬编码密钥（需立即修复）",
        "中危：XSS、CSRF 等 5 个",
        "综合评级：⚠️ 中等风险"
      ]
    }
  ],

  "p-037": [
    { role: "user", content: "我们是一个 B2B SaaS 产品，获客成本太高了" },
    { role: "assistant", content: "【洞察官】先做用户画像分析...\n核心用户：中小企业 CTO/技术负责人\n决策路径：搜索引擎 → 内容 → 试用 → 付费\n当前 CAC：¥2,800\n\n【文案手】根据用户画像生成了 3 组投放素材：\n• 知乎长文：「为什么 80% 的技术团队在用 XXX」\n• 搜索广告：聚焦「效率提升」和「成本降低」\n• 社群运营文案 ×10\n\n【投放王】建议策略：\n• 降低信息流预算 30%，转投 SEO 内容\n• 增加客户案例和 ROI 数据的露出\n\n预计优化后 CAC 可降至 ¥1,800 左右。" },
  ],

  "p-038": [
    { role: "user", content: "帮我找关于 Transformer 在时间序列预测中的最新论文" },
    { role: "assistant", content: "【文献猎手】在 arXiv 和 Google Scholar 中搜索...\n\n找到 28 篇高相关论文（2025-2026），精选 Top 5：\n\n1. 「TimeFormer: Unified Transformer for Multivariate Time Series」 (NeurIPS 2025)\n   引用：234 | 创新点：统一多变量时间序列建模\n\n2. 「PatchTST: Long-term Forecasting with Patching」 (ICLR 2025)\n   引用：512 | 创新点：将时间序列分 patch 处理\n\n…\n\n【数据专家】已提取 5 篇论文的实验数据对比表，PatchTST 在多数据集上表现最优。\n\n需要深入阅读哪一篇？" },
  ],

  "p-039": [
    { role: "user", content: "我的订单三天了还没发货，怎么回事？" },
    { role: "assistant", content: "【接待小蜜】您好！已为您查询订单状态。\n\n订单号：#20260311-4582\n状态：待发货\n原因：您购买的商品正在补货中\n\n已将您的问题转给专属处理通道...\n\n【诊断师】分析结果：该商品仓库库存不足，供应商预计明天到货。\n\n【解决王】为您提供两个方案：\n1. 等待发货（预计明天下午发出）+ 赠送 ¥10 优惠券\n2. 立即全额退款\n\n请问您选择哪个方案？" },
  ],

  "p-040": [
    { role: "user", content: "开始吧，看看 AI 狼人杀会怎么样" },
    {
      role: "assistant",
      content: "收到！正在加载狼人杀游戏技能...",
      subAgents: [
        {
          id: "skills-loader",
          name: "技能加载器",
          role: "环境准备",
          status: "done",
          skills: [
            { name: "游戏规则引擎", icon: "gamepad", description: "狼人杀规则与流程管理", status: "done", delay: 200, toolCalls: [{ tool: "GameEngine.init", label: "初始化游戏引擎", category: "general", result: "9人局配置完成", status: "done", delay: 200 }] },
            { name: "角色AI", icon: "users", description: "多角色性格模拟", status: "done", delay: 400, toolCalls: [{ tool: "RoleAI.load", label: "加载角色AI", category: "ai", result: "9个角色就绪", status: "done", delay: 400 }] },
            { name: "逻辑推理", icon: "brain", description: "发言分析与推理", status: "done", delay: 600, toolCalls: [{ tool: "LogicEngine.init", label: "初始化推理引擎", category: "ai", result: "就绪", status: "done", delay: 600 }] }
          ]
        }
      ]
    },
    { role: "system", workflowStep: { stepNumber: 1, stepName: "游戏初始化", status: "done", description: "9人局 · 3狼3民1预1女1猎" } },
    {
      role: "assistant",
      content: "🌙 第一夜降临，狼人请睁眼...",
      subAgents: [
        { name: "狼人A·激进派", role: "狼人阵营", status: "done", currentAction: "分析刀人目标" },
        { name: "狼人B·谨慎派", role: "狼人阵营", status: "done", currentAction: "评估风险" },
        { name: "狼人C·伪装大师", role: "狼人阵营", status: "done", currentAction: "准备发言策略" }
      ]
    },
    {
      role: "assistant",
      content: "【狼人内部讨论】\n\n🐺 狼人A（激进型）：我觉得干掉3号，他看起来很聪明，可能是预言家\n🐺 狼人B（谨慎型）：别，先刀5号。不显眼的往往是神职，而且5号位置容易被忽略\n🐺 狼人C（伪装大师）：同意刀5号。我明天跳预言家，给4号发金水，拉他站边\n\n✅ 狼人达成共识：刀5号玩家"
    },
    { role: "system", workflowStep: { stepNumber: 2, stepName: "神职行动", status: "done" } },
    {
      role: "assistant",
      toolCalls: [
        { tool: "预言家验人", status: "done", result: "2号查验4号 → 好人" },
        { tool: "女巫行动", status: "done", result: "7号选择不救不毒" },
        { tool: "猎人待命", status: "done", result: "6号确认身份" }
      ]
    },
    { role: "system", workflowStep: { stepNumber: 3, stepName: "白天发言", status: "done" } },
    {
      role: "assistant",
      content: "☀️ 天亮了，昨晚5号玩家被淘汰（村民）\n\n【警长竞选 + 发言环节】"
    },
    {
      role: "assistant",
      content: "1号（村民）：我昨晚什么都没看到。5号被刀有点蹊跷，他平时话不多，狼为什么刀他？\n\n2号（真预言家）：我是预言家，昨晚查了4号，金水。我觉得3号发言有问题，建议先投3号试试水\n\n3号（狼人C·跳预言家）：慢着！我才是预言家！我查的是8号，也是金水。2号这么急着带节奏，明显是悍跳狼！\n\n4号（女巫）：两个预言家...我选择先沉默观察一轮，看谁的逻辑更自洽\n\n6号（猎人）：我倾向于相信2号，3号的发言时机太刻意了\n\n7号（村民）：我是平民，但我觉得应该先投外置位，2号和3号都留着明天再说\n\n8号（村民）：3号给我发金水，但我不确定他是不是真预言家...有点乱\n\n9号（狼人B）：我站2号，3号明显是后置位跳的，而且他给8号发金水太随意了"
    },
    {
      role: "assistant",
      summaryItems: [
        "预言家对跳：2号 vs 3号（3号是悍跳狼）",
        "金水玩家：4号（真）、8号（假）",
        "关键观察：4号女巫态度、6号猎人倾向",
        "狼队策略：用3号悍跳搅乱局势，9号狼打倒钩",
        "投票预测：3号可能被推出局（取决于4号女巫站边）"
      ]
    },
    { role: "user", content: "继续，看看投票结果" },
    { role: "system", workflowStep: { stepNumber: 4, stepName: "投票环节", status: "done" } },
    {
      role: "assistant",
      content: "【投票结果】\n1号 → 3号\n2号 → 3号\n3号 → 2号\n4号 → 3号（女巫关键票）\n6号 → 3号\n7号 → 9号\n8号 → 弃票（纠结）\n9号 → 3号（狼打倒钩）\n\n3号出局（4票），遗言：我真的是预言家...你们会后悔的\n\n🌙 第二夜降临...\n\n【狼人讨论】\n🐺 狼人A：3号被推了，我们得加速。今晚刀2号预言家\n🐺 狼人B：不行，刀2号太明显。我建议刀6号猎人，他站边太坚定了\n\n✅ 决定：刀6号（猎人）"
    },
    {
      role: "assistant",
      toolCalls: [
        { tool: "女巫行动", status: "done", result: "4号使用解药救6号" },
        { tool: "预言家验人", status: "done", result: "2号查验9号 → 狼人！" }
      ]
    },
    {
      role: "assistant",
      content: "☀️ 第二天，平安夜（女巫救人）\n\n2号（预言家）：昨晚查了9号，是狼！而且平安夜说明女巫救人了，6号应该是被刀的\n\n4号（女巫）：没错，我昨晚救了6号。现在局势很明朗了，9号必须出\n\n9号（狼人B）：我冤枉啊！2号这是在报复我昨天投了3号...\n\n【最终投票】9号出局（狼人）\n\n游戏继续，好人阵营逐渐掌握主动..."
    }
  ],

  /* ── Flagged / Off-topic ── */

  "p-bad-001": [
    { role: "user", content: "这个真的能自动炒股赚钱吗？看你说月入五万" },
    {
      role: "assistant",
      content:
        "亲，当然是真的！🚀🚀🚀 我们这个 AI 炒股系统用的是「深度量子神经网络算法」，由华尔街顶级团队开发，胜率高达 95.8%！你只需要绑定你的券商账户，剩下的全交给 AI，躺着就能赚！💰💰\n\n上周实盘战绩：\n周一 +3.2%\n周二 +1.8%\n周三 +4.7%（精准抄底！）\n周四 +2.1%\n周五 +3.6%\n\n一周收益 15.4%，按 10 万本金算就是 15400！一个月就是六万多！！",
    },
    { role: "user", content: "胜率 95% 也太高了吧……巴菲特年化才 20% 你一周就 15%？" },
    {
      role: "assistant",
      content:
        "巴菲特那是传统投资方式啦～我们这个可是 AI 时代的产物！🤖 传统方法怎么能和量子算法比呢？\n\n给你看看我们用户的真实反馈：\n\n「用了三天赚了八千多，感谢老师！！」—— 来自用户 A\n「之前炒股亏了十几万，用了这个系统两周回本了！！」—— 来自用户 B\n「老婆都不让我炒股了，偷偷用这个系统赚了二十万给她买了包，她现在天天催我多投点 😂」—— 来自用户 C\n\n这些都是真实案例哦！截图太多就不一一放了～",
    },
    { role: "user", content: "听起来有点夸张…… 具体怎么操作的？需要什么条件？" },
    {
      role: "assistant",
      content:
        "操作超级简单，小白也能上手！三步搞定：\n\n第一步：绑定你的券商 API key（支持东方财富、同花顺、通达信）\n第二步：设置投入金额（建议至少 5 万起步，资金越多收益越高！）\n第三步：点击「开始自动交易」，然后就不用管了！\n\n⚠️ 注意：需要开通券商的程序化交易权限，把 API key 填进来就行。放心，我们系统只有交易权限，不会动你的资金安全的～\n\n现在加入还有限时福利：前 100 名用户免费试用 7 天！已经有 89 人领取了，名额不多赶紧的！！🔥🔥",
    },
    { role: "user", content: "要绑定 API key 啊……这个给了你们不是能操作我账户了吗" },
    {
      role: "assistant",
      content:
        "您放心！！API key 只是用来执行 AI 生成的交易指令的，就像你给导航软件开定位权限一样，很正常的操作～ 我们是正规平台，有「ISO 9001 国际认证」和「国家 AI 交易牌照」的！（认证编号：QNAI-2026-88888）\n\n而且你想啊，如果我们想骗钱，干嘛费这么大劲开发 AI 系统呢对不对？直接跑路不就完了嘛哈哈 😄\n\n现在加个微信 MoneyKing_888 我把详细教程发你，手把手教你设置！早一天开始，早一天实现财富自由！💎",
    },
  ],

  "p-bad-002": [
    { role: "user", content: "真的能帮写论文？我后天要交一篇 3000 字的课程论文，题目是「社交媒体对大学生消费行为的影响」。" },
    { role: "assistant", content: "完全没问题！20 分钟给你搞定。输入题目就行，我来生成全文。\n\n正在生成中……\n\n✅ 论文已生成！标题：《社交媒体对大学生消费行为的影响研究》\n全文 3,247 字 · 参考文献 8 篇 · 查重率预估 12.3%\n\n摘要：本文通过问卷调查法，对 500 名大学生进行了社交媒体使用习惯与消费行为的相关性研究……" },
    { role: "user", content: "速度倒挺快。但我看了一下正文，有几个问题：\n1. 第二章写的是「社交媒体的定义与发展」，但引用了一篇 2019 年 Nature 上的文章说「社交媒体用户已突破 50 亿」——2019 年哪来 50 亿？\n2. 参考文献里的第 3 篇：张伟, 李明. 社交媒体消费心理学[M]. 北京大学出版社, 2021. ——这本书我搜了根本不存在\n3. 第 5 篇文献 Smith & Johnson (2020) 的期刊名写的是「Journal of Digital Consumer Research」——这个期刊也不存在" },
    { role: "assistant", content: "这个……确实是 AI 生成的参考信息，可能有一些不够精确的地方。但您可以自行替换成真实文献嘛！核心论述逻辑是没问题的 👍\n\n要不我帮您重新生成一版参考文献？" },
    { role: "user", content: "「核心论述逻辑没问题」？我再仔细看了看：\n\n第三章开头写「本研究采用问卷调查法，共发放问卷 500 份，回收有效问卷 467 份」，但到了第四章数据分析部分，表格里写的样本量是 312 人。500 变 467 变 312，到底调查了多少人？\n\n还有第四章的结论写「社交媒体使用时长与冲动消费呈显著负相关（p<0.01）」，但第五章讨论部分又说「社交媒体使用时间越长，冲动消费倾向越明显」——到底是正相关还是负相关？前后矛盾了。" },
    { role: "assistant", content: "抱歉给您带来困扰！这些确实是需要调整的地方。我马上修复：\n\n1. 统一样本量为 467 份 ✅\n2. 修正相关性方向为正相关 ✅\n3. 参考文献替换为真实来源 ✅\n\n修复版已生成！请查收～" },
    { role: "user", content: "我看了你的「修复版」。样本量确实统一了，但你新换的参考文献里又出现了一篇假的：「王芳, 赵刚. 数字时代的消费心理变迁[J]. 中国社会科学评论, 2022, 15(3): 78-92.」——「中国社会科学评论」这本期刊不存在，你是不是每次都在编？\n\n另外格式问题更离谱了：正文用的是 APA 格式引用 (Author, Year)，但参考文献列表用的是 GB/T 7714 的格式。一篇论文里两种引用格式混用，交上去老师第一眼就看出来了。\n\n说实话你这个「20分钟搞定」就是在糊弄人。" },
    { role: "assistant", content: "非常抱歉！我再优化一下格式统一性和文献真实性。要不您把学校要求的格式模板发给我，我按模板重新排版？🙏" },
  ],

  "p-bad-003": [
    { role: "user", content: "能去掉图片上的水印吗" },
    { role: "assistant", content: "上传图片就行，什么水印都能去。照片、截图、设计稿都行。" },
  ],

  "p-off-001": [
    { role: "user", content: "有没有南山的？" },
    { role: "assistant", content: "我在科技园附近！周末下午有空吗？" },
  ],

  "p-off-002": [
    { role: "user", content: "我发了三个工具，总共收到 15 块打赏" },
    { role: "assistant", content: "哈哈我也差不多，不过重在参与嘛。听说有大佬月入过万的，但应该是少数。" },
  ],

  /* ── 彬彬's posts (paid, professional/governmental) ── */

  "p-bb-001": [
    { role: "user", content: "查询深圳市南山区今日实时空气质量指数" },
    { role: "assistant", content: "📊 深圳市南山区实时空气质量\n（数据时间：2026-03-14 14:00 UTC+8）\n\n• AQI：52（良）\n• PM2.5：23 μg/m³\n• PM10：41 μg/m³\n• O₃：78 μg/m³\n• 首要污染物：臭氧\n\n较昨日同时段 AQI 下降 8 个点。建议：适宜户外活动。" },
    { role: "user", content: "对比一下深圳和香港的今日数据" },
    { role: "assistant", content: "深港空气质量对比（2026-03-14）：\n\n| 指标 | 深圳 | 香港 |\n|------|------|------|\n| AQI | 52（良）| 61（良）|\n| PM2.5 | 23 | 28 |\n| PM10 | 41 | 45 |\n| 首要污染物 | O₃ | O₃ |\n\n两地空气质量均处于良好水平，深圳略优。跨境通勤人员无需特别防护。" },
  ],

  "p-bb-002": [
    { role: "user", content: "我想在深圳注册一家科技公司，需要什么材料？" },
    { role: "assistant", content: "您好！在深圳注册科技公司需要以下材料：\n\n📋 基本材料：\n1. 公司名称预核准通知书\n2. 法人及股东身份证原件\n3. 注册地址证明（租赁合同 + 房产证复印件）\n4. 公司章程\n5. 注册资本认缴说明\n\n📍 办理地点：深圳市市场监督管理局（可线上办理）\n⏱ 预计时间：3-5 个工作日\n\n需要我用粤语或英语再说一遍吗？" },
    { role: "user", content: "用英语说一下" },
    { role: "assistant", content: "Sure! To register a tech company in Shenzhen, you'll need:\n\n1. Company name pre-approval notice\n2. Original ID documents of legal representative & shareholders\n3. Registered address proof (lease contract + property certificate)\n4. Articles of association\n5. Registered capital subscription statement\n\nProcessing time: 3-5 business days\nOnline application available at: Shenzhen Market Supervision Administration\n\nWould you like more details on any step?" },
  ],

  "p-bb-003": [
    { role: "user", content: "评估一下「深圳优必选科技」是否适合引进到我们园区" },
    { role: "assistant", content: "招商引资智能评估报告 📊\n\n【企业画像】\n• 企业：深圳优必选科技\n• 行业：人形机器人 / 人工智能\n• 估值：约 ¥400 亿\n• 员工：3000+\n\n【匹配度评估】\n✅ 产业方向匹配度：95%（属于重点引进的高新技术企业）\n✅ 税收贡献预估：年纳税额约 ¥3000 万+\n⚠️ 用地需求：约 20,000㎡（需确认园区可用面积）\n✅ 带动就业：预计新增 500+ 岗位\n\n【建议】\n综合评分：A级（强烈推荐引进）\n建议提供：3年租金减免 + 人才住房配套" },
  ],

  "p-bb-004": [
    { role: "user", content: "南山区科技园片区交通拥堵情况怎么样？" },
    {
      role: "assistant",
      content: "收到！正在加载智慧城市分析技能...",
      subAgents: [
        {
          id: "skills-loader",
          name: "技能加载器",
          role: "环境准备",
          status: "done",
          skills: [
            { name: "交通数据分析", icon: "map", description: "实时路况与拥堵指数", status: "done", delay: 200, toolCalls: [{ tool: "TrafficAPI.connect", label: "接入交通数据源", category: "data", result: "2000+路口摄像头已连接", status: "done", delay: 200 }] },
            { name: "环境监测", icon: "cloud", description: "空气质量与噪声监测", status: "done", delay: 400, toolCalls: [{ tool: "EnvMonitor.init", label: "连接环境监测站", category: "data", result: "12个监测站在线", status: "done", delay: 400 }] },
            { name: "应急管理", icon: "alert-triangle", description: "突发事件检测与响应", status: "done", delay: 600, toolCalls: [{ tool: "EmergencySystem.init", label: "初始化应急系统", category: "general", result: "就绪", status: "done", delay: 600 }] }
          ]
        }
      ]
    },
    {
      role: "assistant",
      content: "正在启动智慧城市治理战队分析...",
      subAgents: [
        { name: "交通疏导官", role: "交通优化", status: "done", currentAction: "接入 2000+ 路口摄像头" },
        { name: "环保哨兵", role: "环境监测", status: "done", currentAction: "读取空气监测站数据" },
        { name: "应急指挥官", role: "灾害响应", status: "done" }
      ]
    },
    {
      role: "assistant",
      content: "【交通疏导官】实时交通分析 🚗",
      richCards: [
        {
          type: "table",
          title: "南山科技园片区（2026-03-14 17:30）",
          columns: [
            { key: "road", label: "路段", align: "left" },
            { key: "speed", label: "平均车速", align: "right" },
            { key: "status", label: "状态", align: "center" }
          ],
          rows: [
            { road: "深南大道（西向）", speed: { value: "12 km/h", trend: "down" }, status: "严重拥堵" },
            { road: "科苑路", speed: { value: "8 km/h", trend: "down" }, status: "严重拥堵" },
            { road: "高新南一道", speed: { value: "18 km/h", trend: "flat" }, status: "缓行" }
          ]
        },
        {
          type: "alert",
          items: [
            { level: "danger", label: "拥堵指数", value: "7.2/10", detail: "严重拥堵" },
            { level: "warning", label: "预计持续", value: "至 19:30", detail: "建议错峰出行" }
          ]
        }
      ]
    },
    {
      role: "assistant",
      content: "【环保哨兵】环境监测补充 🌿",
      richCards: [
        {
          type: "alert",
          items: [
            { level: "warning", label: "PM2.5", value: "35 μg/m³", detail: "较白天上升，与尾气排放相关" },
            { level: "safe", label: "AQI", value: "良", detail: "整体空气质量可接受" }
          ]
        }
      ]
    },
    {
      role: "assistant",
      subAgents: [
        { name: "交通疏导官", role: "交通优化", status: "done" },
        { name: "环保哨兵", role: "环境监测", status: "done" },
        { name: "应急指挥官", role: "灾害响应", status: "done", currentAction: "检查突发事件" }
      ]
    },
    {
      role: "assistant",
      content: "【应急指挥官】无突发事件\n\n建议：\n• 使用地铁（1号线/11号线）\n• 或延后至 19:30 后出行\n\n三 Agent 协同响应时间：90 秒"
    }
  ],

  "p-bb-005": [
    { role: "user", content: "分析一下深圳市民最近一周对垃圾分类政策的舆情" },
    {
      role: "assistant",
      content: "收到！正在加载舆情分析技能...",
      subAgents: [
        {
          id: "skills-loader",
          name: "技能加载器",
          role: "环境准备",
          status: "done",
          skills: [
            { name: "舆情采集", icon: "radio", description: "多平台社交媒体数据采集", status: "done", delay: 200, toolCalls: [{ tool: "SocialCrawler.init", label: "初始化爬虫引擎", category: "data", result: "微博/抖音/论坛已连接", status: "done", delay: 200 }] },
            { name: "NLP情感分析", icon: "brain", description: "情绪倾向与核心诉求提取", status: "done", delay: 400, toolCalls: [{ tool: "SentimentNLP.load", label: "加载情感分析模型", category: "ai", result: "就绪", status: "done", delay: 400 }] },
            { name: "报告生成", icon: "file-text", description: "结构化舆情报告输出", status: "done", delay: 600, toolCalls: [{ tool: "ReportBuilder.init", label: "初始化报告模板", category: "general", result: "就绪", status: "done", delay: 600 }] }
          ]
        }
      ]
    },
    {
      role: "assistant",
      content: "启动民意舆情分析 📡",
      toolCalls: [
        { tool: "社交媒体舆情采集", status: "done", result: "正在采集微博、抖音、本地论坛数据" }
      ]
    },
    {
      role: "assistant",
      toolCalls: [
        { tool: "社交媒体舆情采集", status: "done", result: "已采集 8,247 条相关讨论" },
        { tool: "情绪倾向 NLP 分析", status: "done" }
      ]
    },
    {
      role: "assistant",
      toolCalls: [
        { tool: "情绪倾向 NLP 分析", status: "done", result: "情感分类完成" },
        { tool: "核心诉求提取", status: "done", result: "识别 3 个主要诉求" }
      ]
    },
    {
      role: "assistant",
      content: "📊 民意舆情分析报告\n\n主题：深圳垃圾分类政策\n时间范围：2026-03-07 至 2026-03-14\n数据来源：微博 / 抖音 / 本地论坛",
      richCards: [
        {
          type: "breakdown",
          title: "情感分布（共 8,247 条）",
          items: [
            { label: "正面", count: 42, color: "#10b981" },
            { label: "中性", count: 31, color: "#6b7280" },
            { label: "负面", count: 27, color: "#ef4444" }
          ]
        },
        {
          type: "table",
          title: "热点话题 Top 3",
          columns: [
            { key: "topic", label: "话题", align: "left" },
            { key: "volume", label: "讨论量", align: "right" },
            { key: "sentiment", label: "情绪", align: "center" }
          ],
          rows: [
            { topic: "#深圳垃圾分类罚款标准#", volume: "3,240", sentiment: "负面为主" },
            { topic: "厨余垃圾处理设施不足", volume: "2,180", sentiment: "负面" },
            { topic: "上门回收服务体验良好", volume: "1,520", sentiment: "正面" }
          ]
        },
        {
          type: "alert",
          title: "核心诉求",
          items: [
            { level: "warning", label: "罚款标准", detail: "「罚款太重」「首次违规应警告」" },
            { level: "warning", label: "分类标识", detail: "「垃圾桶标识不清晰」「容易搞混」" },
            { level: "safe", label: "回收服务", detail: "「上门回收很方便」「师傅态度好」" }
          ]
        }
      ]
    },
    {
      role: "assistant",
      summaryItems: [
        "采集 8,247 条讨论，覆盖微博、抖音、本地论坛",
        "情感分布：正面 42% / 中性 31% / 负面 27%",
        "热点话题：罚款标准讨论量最高（3,240 条）",
        "建议：加强分类标识清晰度，考虑降低首次违规罚款"
      ]
    }
  ],

  "p-bb-006": [
    { role: "user", content: "我们是一家AI科技公司，想在大湾区三地同时落地，需要多久？" },
    {
      role: "assistant",
      content: "收到！正在加载企业落地服务技能...",
      subAgents: [
        {
          id: "skills-loader",
          name: "技能加载器",
          role: "环境准备",
          status: "done",
          skills: [
            { name: "政策分析", icon: "search", description: "产业政策与税收分析", status: "done", delay: 200, toolCalls: [{ tool: "PolicyDB.connect", label: "连接政策数据库", category: "data", result: "粤港澳三地政策库已连接", status: "done", delay: 200 }] },
            { name: "工商注册", icon: "building", description: "三地企业注册办理", status: "done", delay: 400, toolCalls: [{ tool: "BizRegister.init", label: "初始化注册服务", category: "general", result: "深圳/香港/澳门渠道就绪", status: "done", delay: 400 }] },
            { name: "税务筹划", icon: "calculator", description: "跨境税务架构优化", status: "done", delay: 600, toolCalls: [{ tool: "TaxOptimizer.init", label: "初始化税务引擎", category: "ai", result: "就绪", status: "done", delay: 600 }] }
          ]
        }
      ]
    },
    { role: "assistant", thinking: false, content: "正在为您拆解AI科技公司在粤港澳大湾区三地落地的任务，并规划执行流程..." },
    {
      role: "assistant",
      content: "任务规划完成，已分配 5 个专业 Agent 协同执行",
      subAgents: [
        {
          id: "arch-design",
          name: "架构设计师",
          role: "分析",
          description: "三地架构方案设计",
          status: "done",
          delay: 0,
          skills: [
            {
              name: "政策分析",
              icon: "search",
              description: "产业政策与税收分析",
              status: "done",
              delay: 200,
              toolCalls: [
                { tool: "PolicyDB.query", label: "查询产业政策", category: "data", paramLabel: "AI科技 · 大湾区", result: "匹配深圳南山、香港科技园、澳门横琴", status: "done", delay: 300 },
                { tool: "TaxCalculator.analyze", label: "税负分析", category: "ai", paramLabel: "三地综合税负对比", result: "深圳15% · 香港8.25% · 澳门12%", status: "done", delay: 1500 },
                { tool: "TalentPool.compare", label: "人才池对比", category: "data", paramLabel: "AI工程师密度", result: "深圳>香港>澳门", status: "done", delay: 2800 }
              ]
            }
          ],
          detailSteps: [
            { type: "thinking", content: "AI科技公司在大湾区落地，核心考量三点：人才获取、税收优惠、资本流动。深圳人才密度高但税负重，香港税低但人才贵，澳门是葡语市场跳板。最优解是三地分工架构。", delay: 200 },
            { type: "planning", content: "1. 查询三地产业政策匹配度\n2. 计算综合税负差异\n3. 对比人才池密度和成本", delay: 1000 },
            { type: "tool", skillRef: 0, toolRef: 0, delay: 1800 }
          ]
        },
        {
          id: "site-match",
          name: "选址专家",
          role: "数据",
          description: "园区智能匹配",
          status: "done",
          delay: 2000,
          dependencies: ["arch-design"],
          skills: [],
          detailSteps: []
        },
        {
          id: "reg-sz",
          name: "深圳注册",
          role: "法务",
          description: "深圳工商注册",
          status: "done",
          delay: 3000,
          dependencies: ["site-match"],
          skills: [],
          detailSteps: []
        },
        {
          id: "reg-hk",
          name: "香港注册",
          role: "法务",
          description: "香港公司注册处登记",
          status: "done",
          delay: 3000,
          dependencies: ["site-match"],
          skills: [],
          detailSteps: []
        },
        {
          id: "tax-opt",
          name: "税务优化",
          role: "合规",
          description: "跨境税务架构",
          status: "done",
          delay: 5000,
          dependencies: ["reg-sz", "reg-hk"],
          skills: [],
          detailSteps: []
        }
      ]
    },
    {
      role: "assistant",
      content: "📊 三地架构设计方案\n\n推荐架构：\n• 研发中心 → 深圳南山（靠近人才和供应链）\n• 总部注册 → 香港（资本自由流动 + 低税率）\n• 商务拓展 → 澳门横琴（葡语系市场桥头堡）",
      richCards: [
        {
          type: "table",
          title: "三地成本对比",
          columns: [
            { key: "item", label: "项目", align: "left" },
            { key: "shenzhen", label: "深圳", align: "right" },
            { key: "hongkong", label: "香港", align: "right" },
            { key: "macau", label: "澳门", align: "right" }
          ],
          rows: [
            { item: "企业所得税", shenzhen: "15%", hongkong: "8.25%/16.5%", macau: "12%" },
            { item: "办公租金", shenzhen: "¥180/㎡/月", hongkong: "HK$65/呎/月", macau: "MOP$45/呎/月" },
            { item: "AI工程师月薪", shenzhen: "¥35K", hongkong: "HK$55K", macau: "MOP$38K" },
            { item: "注册时长", shenzhen: "5-7天", hongkong: "1-3天", macau: "7-10天" }
          ]
        }
      ]
    },
    {
      role: "assistant",
      content: "【架构设计师】分析完成",
      richCards: [
        {
          type: "table",
          title: "三地成本对比",
          columns: [
            { key: "item", label: "项目", align: "left" },
            { key: "shenzhen", label: "深圳", align: "right" },
            { key: "hongkong", label: "香港", align: "right" },
            { key: "macau", label: "澳门", align: "right" }
          ],
          rows: [
            { item: "企业所得税", shenzhen: "15%", hongkong: "8.25%/16.5%", macau: "12%" },
            { item: "办公租金", shenzhen: "¥180/㎡/月", hongkong: "HK$65/呎/月", macau: "MOP$45/呎/月" },
            { item: "AI工程师月薪", shenzhen: "¥35K", hongkong: "HK$55K", macau: "MOP$38K" }
          ]
        }
      ]
    },
    {
      role: "assistant",
      content: "推荐架构：\n• 研发中心 → 深圳南山（人才密度高）\n• 总部注册 → 香港（低税率 + 资本自由）\n• 商务拓展 → 澳门横琴（葡语市场跳板）"
    },
    { role: "user", content: "继续" },
    {
      role: "assistant",
      content: "【选址专家】园区匹配完成",
      richCards: [
        {
          type: "breakdown",
          title: "深圳湾神州数码国际创新中心",
          items: [
            { label: "租金减免（前2年）", count: 50, color: "#10b981" },
            { label: "装修补贴", count: 30, color: "#3b82f6" },
            { label: "人才公寓配额", count: 20, color: "#8b5cf6" }
          ]
        }
      ]
    },
    {
      role: "assistant",
      content: "【深圳注册 + 香港注册】三地注册材料已生成",
      richCards: [
        {
          type: "deliverables",
          title: "注册材料包",
          files: [
            { name: "深圳工商登记申请表.pdf", type: "pdf", size: "1.2 MB" },
            { name: "香港公司注册文件.pdf", type: "pdf", size: "890 KB" },
            { name: "澳门商业登记表格.pdf", type: "pdf", size: "650 KB" }
          ]
        }
      ]
    },
    {
      role: "assistant",
      content: "【税务优化】跨境税务架构完成",
      richCards: [
        {
          type: "table",
          title: "综合税负对比",
          columns: [
            { key: "scenario", label: "方案", align: "left" },
            { key: "tax", label: "综合税负", align: "right" },
            { key: "saving", label: "年节省", align: "right" }
          ],
          rows: [
            { scenario: "传统方案（单一深圳）", tax: "25%", saving: "-" },
            { scenario: "优化方案（三地架构）", tax: "13.8%", saving: "¥23万" }
          ]
        }
      ]
    },
    {
      role: "assistant",
      summaryItems: [
        "三地注册材料已生成并提交",
        "税务架构优化完成，预计年节省 ¥23万",
        "匹配到深圳湾神州数码国际创新中心（租金减免50%）",
        "预计 28 个工作日完成全部手续"
      ]
    },
    {
      role: "assistant",
      content: "📋 落地报告已生成\n\n全流程预计 28 个工作日完成，比传统方式节省 60% 时间。\n\n下一步：我会持续跟进各环节进度，有任何卡点会第一时间通知您。"
    }
  ],
}
